import { useState, useEffect, useRef, useCallback } from "react";
import { saveData, loadData, exportToJSON, importFromJSON, clearData } from "./db";
import {
  IconCheck, IconX, IconClipboard, IconMoney, IconChart,
  IconZap, IconCalendar, IconRefresh, IconTarget, IconSearch,
  IconCreditCard, IconBank, IconTrendingUp, IconLightbulb, IconEdit,
  IconSprout, IconLeaf, IconTree, IconCrown, IconBaby,
  IconFamily, IconPhone, IconSparkles, IconSave, IconUpload,
  IconDownload, IconTrash, IconUser, IconCamera, IconBell, IconRotateCw,
  IconCircle, IconDot
} from "./icons";

/* ═══════════════════════════════════════════════
   PWA UPDATE HOOK - 后台静默更新（防重复刷新）
═══════════════════════════════════════════════ */
function usePWAUpdate() {
  const [offlineReady, setOfflineReady] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const isRefreshing = useRef(false);
  const checkedUpdateKey = 'kidfi_sw_checked';

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // 监听 Service Worker 消息
      const handleMessage = (event) => {
        if (event.data && event.data.type === 'SW_OFFLINE_READY') {
          setOfflineReady(true);
          setTimeout(() => setOfflineReady(false), 3000);
        }
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);

      // 注册 Service Worker 更新检查
      navigator.serviceWorker.ready.then((registration) => {
        // 检查是否已经有等待中的 Service Worker（真正的新版本）
        if (registration.waiting) {
          // 检查本次会话是否已经提示过
          const hasChecked = sessionStorage.getItem(checkedUpdateKey);
          if (!hasChecked) {
            setUpdateAvailable(true);
            sessionStorage.setItem(checkedUpdateKey, 'true');
          }
        }

        // 每 30 分钟检查一次更新
        const interval = setInterval(() => {
          registration.update();
        }, 30 * 60 * 1000);

        // 监听新的 Service Worker 安装
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              // 只有当新 Service Worker 真正安装完成且在等待时才提示
              if (newWorker.state === 'installed' && registration.waiting) {
                // 检查本次会话是否已经提示过
                const hasChecked = sessionStorage.getItem(checkedUpdateKey);
                if (!hasChecked) {
                  setUpdateAvailable(true);
                  sessionStorage.setItem(checkedUpdateKey, 'true');
                }
              }
            });
          }
        });

        return () => clearInterval(interval);
      });

      // 监听 controllerchange 事件（Service Worker 已切换）
      const handleControllerChange = () => {
        const needsRefresh = sessionStorage.getItem('kidfi_sw_update') === 'pending';
        if (needsRefresh && !isRefreshing.current) {
          isRefreshing.current = true;
          sessionStorage.removeItem('kidfi_sw_update');
          sessionStorage.removeItem(checkedUpdateKey);
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      };
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      };
    }
  }, []);

  // 应用更新
  const applyUpdate = useCallback(() => {
    if (!isRefreshing.current && 'serviceWorker' in navigator) {
      isRefreshing.current = true;
      setUpdateAvailable(false);
      
      sessionStorage.setItem('kidfi_sw_update', 'pending');
      
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    }
  }, []);

  return { offlineReady, updateAvailable, applyUpdate };
}

/*
  UI/交互优化要点（简洁清爽版）
  - 统一浅色系主题（背景/卡片/边框/主色）
  - 抽象 Card / Button / Input / ModalShell 组件，减少重复样式与花哨渐变
  - 减少 Emoji/炫光/重阴影：保留少量提示性图标，整体更克制
  - 强化可用性：输入框 number 校验、disabled 状态、可点击区域一致、按钮按压反馈
*/

/* ═══════════════════════════════════════════════
   RATE FETCHER
═══════════════════════════════════════════════ */
async function fetchTodayRates() {
  const today = new Date().toISOString().slice(0, 10);
  const cacheKey = `kidfi_rates_${today}`;

  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch (_) {}

  // NOTE: 这里保留原逻辑（Claude + web_search 工具）。
  const prompt = `请帮我查询中国银行（Bank of China）当前最新的人民币存款利率，包括活期年利率和一年期定期年利率。

请用网络搜索获取最新数据，然后严格按照以下JSON格式返回，不要输出任何其他内容：
{"demand":0.0005,"term1y":0.0095,"term3y":0.0125,"source":"中国银行","date":"${today}","note":"自YYYY年MM月DD日起执行"}

其中 demand 是活期年利率（小数，如0.05%写成0.0005），term1y是一年定期年利率，term3y是三年定期年利率。`;

  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 300,
      tools: [{ type: "web_search_20250305", name: "web_search" }],
      messages: [{ role: "user", content: prompt }],
    }),
  });

  const data = await res.json();
  const text = (data.content || [])
    .filter((b) => b.type === "text")
    .map((b) => b.text)
    .join("");

  const match = text.match(/\{[^}]+\}/);
  if (match) {
    try {
      const rates = JSON.parse(match[0]);
      if (typeof rates.demand === "number" && typeof rates.term1y === "number") {
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(rates));
        } catch (_) {}
        return rates;
      }
    } catch (_) {}
  }

  return {
    demand: 0.0005,
    term1y: 0.0095,
    term3y: 0.0125,
    source: "中国银行(缓存)",
    date: today,
    note: "自2025年5月20日起执行",
  };
}

/* ═══════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════ */
const WEEKDAYS = ["一", "二", "三", "四", "五", "六", "日"];

/* ═══════════════════════════════════════════════
   DESIGN TOKENS（简洁浅色）
═══════════════════════════════════════════════ */
const UI = {
  bg: "#f6f7fb",
  panel: "#ffffff",
  text: "#0f172a",
  muted: "#64748b",
  border: "rgba(15,23,42,.10)",
  borderStrong: "rgba(15,23,42,.14)",
  primary: "#4f46e5", // indigo-600
  primarySoft: "rgba(79,70,229,.10)",
  success: "#16a34a",
  warn: "#d97706",
  danger: "#dc2626",

  // bottom-nav / CTA accents
  accentGreen: "#22c55e",
  accentGreenSoft: "rgba(34,197,94,.16)",
  accentCyan: "#06b6d4",
  accentCyanSoft: "rgba(6,182,212,.16)",

  shadow: "0 10px 30px rgba(15,23,42,.06)",
};

/* ═══════════════════════════════════════════════
   UI ATOMS
═══════════════════════════════════════════════ */
function cx(...arr) {
  return arr.filter(Boolean).join(" ");
}

function Card({ className, style, children }) {
  return (
    <div
      className={cx("rounded-2xl", className)}
      style={{
        background: UI.panel,
        border: `1px solid ${UI.border}`,
        boxShadow: UI.shadow,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Pill({ color, bg, children }) {
  return (
    <span
      className="text-xs font-semibold px-2 py-0.5 rounded-full"
      style={{ background: bg, color, border: `1px solid ${color}26` }}
    >
      {children}
    </span>
  );
}

function Button({
  variant = "primary",
  className,
  style,
  disabled,
  children,
  ...props
}) {
  const base =
    "select-none inline-flex items-center justify-center gap-1.5 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition active:scale-[0.98]";

  const map = {
    primary: {
      bg: UI.primary,
      fg: "#fff",
      bd: "transparent",
    },
    ghost: {
      bg: "transparent",
      fg: UI.text,
      bd: UI.borderStrong,
    },
    subtle: {
      bg: "rgba(15,23,42,.04)",
      fg: UI.text,
      bd: UI.border,
    },
    danger: {
      bg: "rgba(220,38,38,.10)",
      fg: UI.danger,
      bd: "rgba(220,38,38,.18)",
    },
    success: {
      bg: "rgba(22,163,74,.12)",
      fg: UI.success,
      bd: "rgba(22,163,74,.18)",
    },
  };
  const v = map[variant] || map.primary;

  return (
    <button
      {...props}
      disabled={disabled}
      className={cx(base, "disabled:opacity-50 disabled:active:scale-100", className)}
      style={{
        background: v.bg,
        color: v.fg,
        border: `1px solid ${v.bd}`,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function Input({ className, style, ...props }) {
  return (
    <input
      {...props}
      className={cx(
        "w-full rounded-xl px-3 py-2.5 text-sm outline-none",
        "focus:ring-2 focus:ring-indigo-500/30",
        className
      )}
      style={{
        background: "rgba(15,23,42,.03)",
        border: `1px solid ${UI.border}`,
        color: UI.text,
        ...style,
      }}
    />
  );
}

function Textarea({ className, style, ...props }) {
  return (
    <textarea
      {...props}
      className={cx(
        "w-full rounded-xl px-3 py-2.5 text-sm outline-none resize-none",
        "focus:ring-2 focus:ring-indigo-500/30",
        className
      )}
      style={{
        background: "rgba(15,23,42,.03)",
        border: `1px solid ${UI.border}`,
        color: UI.text,
        ...style,
      }}
    />
  );
}

function NavIcon({ name, active }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    style: { opacity: active ? 1 : 0.9 },
  };

  switch (name) {
    case "home":
      return (
        <svg {...common}>
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10" />
        </svg>
      );
    case "tasks":
      return (
        <svg {...common}>
          <path d="M9 11l2 2 4-4" />
          <rect x="4" y="4" width="16" height="16" rx="3" />
          <path d="M8 17h8" />
        </svg>
      );
    case "checkin":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="10" />
          <path d="M8 12l2 2 4-4" />
        </svg>
      );
    case "bank":
      return (
        <svg {...common}>
          <path d="M4 10h16" />
          <path d="M6 10v9" />
          <path d="M10 10v9" />
          <path d="M14 10v9" />
          <path d="M18 10v9" />
          <path d="M3 10l9-6 9 6" />
          <path d="M4 19h16" />
        </svg>
      );
    case "credit":
      return (
        <svg {...common}>
          <path d="M12 17l-5 3 1.6-5.7L4 10.8l5.9-.3L12 5l2.1 5.5 5.9.3-4.6 3.5L17 20z" />
        </svg>
      );
    case "log":
      return (
        <svg {...common}>
          <path d="M8 6h13" />
          <path d="M8 12h13" />
          <path d="M8 18h13" />
          <path d="M3 6h1" />
          <path d="M3 12h1" />
          <path d="M3 18h1" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
        </svg>
      );
  }
}

function ModalShell({ title, subtitle, onClose, children, maxH = "88vh" }) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center"
      style={{ background: "rgba(15,23,42,.55)", backdropFilter: "blur(8px)" }}
    >
      <div
        className="w-full max-w-md rounded-t-3xl p-5"
        style={{
          background: UI.panel,
          border: `1px solid ${UI.border}`,
          maxHeight: maxH,
          overflowY: "auto",
        }}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            {subtitle && (
              <div className="text-xs font-semibold" style={{ color: UI.muted }}>
                {subtitle}
              </div>
            )}
            <div className="text-base font-bold truncate" style={{ color: UI.text }}>
              {title}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: "rgba(15,23,42,.06)", color: UI.muted }}
            aria-label="关闭"
          >
            ✕
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   CHECKIN HISTORY MODAL (打卡记录弹窗)
═══════════════════════════════════════════════ */
function CheckinHistoryModal({ task, onClose }) {
  const allCheckins = task.cycleCheckins || [];

  // 按时间倒序排列
  const sortedCheckins = [...allCheckins].sort((a, b) => {
    const dateTimeA = new Date(`${a.date} ${a.time || '00:00'}`);
    const dateTimeB = new Date(`${b.date} ${b.time || '00:00'}`);
    return dateTimeB - dateTimeA;
  });

  return (
    <ModalShell
      title={task.title}
      subtitle={`共 ${sortedCheckins.length} 条打卡记录`}
      onClose={onClose}
      maxH="80vh"
    >
      {sortedCheckins.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-3xl mb-2">📋</div>
          <div className="text-sm" style={{ color: UI.muted }}>暂无打卡记录</div>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedCheckins.map((checkin, idx) => (
            <Card
              key={checkin.id || idx}
              className="overflow-hidden"
              style={{ boxShadow: "0 2px 8px rgba(15,23,42,.04)" }}
            >
              {/* 打卡图片 */}
              {checkin.proof && (
                <div className="w-full">
                  <img
                    src={checkin.proof}
                    alt="打卡凭证"
                    className="w-full"
                    style={{ maxHeight: 200, objectFit: 'cover' }}
                  />
                </div>
              )}

              {/* 备注和时间 */}
              <div className="p-3">
                {checkin.note && (
                  <div className="text-sm mb-2" style={{ color: UI.text }}>
                    {checkin.note}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-xs" style={{ color: UI.muted }}>
                    {checkin.date} {checkin.time}
                  </div>
                  <div 
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: UI.primarySoft, color: UI.primary }}
                  >
                    第{checkin.cycleIndex}周期
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </ModalShell>
  );
}

/* ═══════════════════════════════════════════════
   CHECKIN HISTORY PAGE (打卡记录页面)
═══════════════════════════════════════════════ */
function CheckinHistoryPage({ tasks }) {
  const [timeFilter, setTimeFilter] = useState("all"); // all, week, month

  // 收集所有打卡记录
  const allCheckinRecords = [];

  tasks.forEach(task => {
    if (task.type === "cycle_week" || task.type === "cycle_month") {
      const checkins = task.cycleCheckins || [];
      checkins.forEach(checkin => {
        allCheckinRecords.push({
          id: checkin.id,
          taskId: task.id,
          taskTitle: task.title,
          taskType: task.type,
          proof: checkin.proof,
          note: checkin.note,
          date: checkin.date,
          time: checkin.time,
          cycleIndex: checkin.cycleIndex,
        });
      });
    }
  });

  // 按时间倒序排列
  const sortedRecords = allCheckinRecords.sort((a, b) => {
    const dateTimeA = new Date(`${a.date} ${a.time || '00:00'}`);
    const dateTimeB = new Date(`${b.date} ${b.time || '00:00'}`);
    return dateTimeB - dateTimeA;
  });

  // 根据时间筛选过滤记录
  const filteredRecords = (() => {
    if (timeFilter === "all") return sortedRecords;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (timeFilter === "week") {
      // 获取本周一（0=周日，1=周一）
      const dayOfWeek = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      monday.setHours(0, 0, 0, 0);

      return sortedRecords.filter(record => {
        const recordDate = new Date(record.date);
        return recordDate >= monday;
      });
    }

    if (timeFilter === "month") {
      // 获取本月第一天
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      return sortedRecords.filter(record => {
        const recordDate = new Date(record.date);
        return recordDate >= firstDayOfMonth;
      });
    }

    return sortedRecords;
  })();

  // 筛选按钮配置
  const filterButtons = [
    { key: "all", label: "所有" },
    { key: "week", label: "本周" },
    { key: "month", label: "本月" },
  ];

  return (
    <div className="space-y-4">
      {/* 标题和筛选 */}
      <div className="flex items-center justify-between px-1">
        <div className="text-sm font-bold" style={{ color: UI.text }}>
          打卡记录
        </div>
        <div className="text-xs" style={{ color: UI.muted }}>
          共 {filteredRecords.length} 条
        </div>
      </div>

      {/* 时间筛选按钮 */}
      <div className="flex gap-2 px-1">
        {filterButtons.map((btn) => (
          <button
            key={btn.key}
            onClick={() => setTimeFilter(btn.key)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
            style={{
              background: timeFilter === btn.key ? UI.primary : UI.panel,
              color: timeFilter === btn.key ? "#fff" : UI.muted,
              border: `1px solid ${timeFilter === btn.key ? UI.primary : UI.border}`,
            }}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* 打卡记录列表 */}
      {filteredRecords.length === 0 ? (
        <Card className="p-8 text-center" style={{ boxShadow: "0 2px 8px rgba(15,23,42,.04)" }}>
          <div className="text-4xl mb-3">📋</div>
          <div className="text-sm font-semibold mb-1" style={{ color: UI.text }}>
            暂无打卡记录
          </div>
          <div className="text-xs" style={{ color: UI.muted }}>
            {timeFilter === "all" ? "完成任务打卡后，记录将显示在这里" : "当前筛选条件下没有打卡记录"}
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredRecords.map((record) => (
            <Card
              key={record.id}
              className="overflow-hidden"
              style={{ boxShadow: "0 2px 8px rgba(15,23,42,.04)" }}
            >
              {/* 任务名称 */}
              <div className="px-3 py-2 border-b" style={{ borderColor: UI.border, background: UI.panel }}>
                <div className="text-sm font-bold" style={{ color: UI.text }}>
                  {record.taskTitle}
                </div>
              </div>

              {/* 打卡图片 */}
              {record.proof && (
                <div className="w-full">
                  <img
                    src={record.proof}
                    alt="打卡凭证"
                    className="w-full"
                    style={{ maxHeight: 200, objectFit: 'cover' }}
                  />
                </div>
              )}

              {/* 备注和时间 */}
              <div className="p-3">
                {record.note && (
                  <div className="text-sm mb-2" style={{ color: UI.text }}>
                    {record.note}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-xs" style={{ color: UI.muted }}>
                    {record.date} {record.time}
                  </div>
                  <div 
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: UI.primarySoft, color: UI.primary }}
                  >
                    第{record.cycleIndex}周期
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const Toast = ({ toast }) => {
  if (!toast) return null;
  const bg =
    toast.type === "error"
      ? "rgba(220,38,38,.95)"
      : toast.type === "warn"
      ? "rgba(217,119,6,.95)"
      : "rgba(22,163,74,.95)";
  return (
    <div
      className="fixed top-5 left-1/2 z-[60] px-4 py-2.5 rounded-2xl text-sm font-semibold text-white"
      style={{
        transform: "translateX(-50%)",
        background: bg,
        backdropFilter: "blur(12px)",
        boxShadow: "0 18px 40px rgba(15,23,42,.25)",
        maxWidth: "90vw",
        textAlign: "center",
      }}
    >
      {toast.msg}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   DOMAIN HELPERS
═══════════════════════════════════════════════ */
function calcReturn(investment, yieldRate) {
  return Math.round(investment * yieldRate * 100) / 100;
}

function formatMoney(num) {
  return (Math.round(num * 100) / 100).toFixed(2);
}

const ScoreBadge = ({ score }) => {
  const tier =
    score >= 800
      ? { t: "金", c: "#f59e0b" }
      : score >= 700
      ? { t: "银", c: "#10b981" }
      : { t: "铜", c: "#6366f1" };
  return <Pill color={tier.c} bg={tier.c + "1A"}>{`童信 ${tier.t}`}</Pill>;
};

const StatusPill = ({ status }) => {
  const m = {
    pending: { t: "待审核", c: UI.warn, bg: "rgba(217,119,6,.10)" },
    approved: { t: "已通过", c: UI.success, bg: "rgba(22,163,74,.10)" },
    rejected: { t: "已拒绝", c: UI.danger, bg: "rgba(220,38,38,.10)" },
  };
  const s = m[status] || m.pending;
  return <Pill color={s.c} bg={s.bg}>{s.t}</Pill>;
};

const TypeTag = ({ type }) => {
  const m = {
    short: { t: "单次", c: "#10b981" },
    long: { t: "连续", c: "#6366f1" },
    cycle_week: { t: "周期·周", c: "#d97706" },
    cycle_month: { t: "周期·月", c: "#db2777" },
  };
  const d = m[type] || m.short;
  return <Pill color={d.c} bg={d.c + "14"}>{d.t}</Pill>;
};

/* ═══════════════════════════════════════════════
   ACCEPT TASK MODAL
═══════════════════════════════════════════════ */
function AcceptTaskModal({ task, walletBalance, onClose, onAccept }) {
  const [amount, setAmount] = useState("");
  const [cycles, setCycles] = useState("1");
  const num = Number(amount);
  const cyclesNum = Number(cycles);
  const validAmt = Number.isFinite(num) && num > 0 && num <= walletBalance;
  const validCycles = Number.isFinite(cyclesNum) && cyclesNum > 0;
  
  const isCycle = task.type === "cycle_week" || task.type === "cycle_month";
  const cycleUnit = task.type === "cycle_week" ? "周" : "月";
  
  // 复利计算：(本金 × (1 + 利率)^周期数) - 本金
  const expectedReturn = validAmt && validCycles && isCycle
    ? Math.round((num * Math.pow(1 + task.yieldRate, cyclesNum) - num) * 100) / 100
    : validAmt && validCycles
    ? calcReturn(num, task.yieldRate)
    : 0;
  const totalExpected = validAmt && validCycles ? num + expectedReturn : 0;

  const typeInfo =
    {
      short: "完成后提交凭证，审核通过即可结算收益",
      long: `坚持 ${task.totalDays} 天每日打卡，全部通过后结算收益`,
      cycle_week: `每周完成选定的 ${(task.requiredDays || []).length} 个打卡日，完成后结算一次`,
      cycle_month: `每月完成选定的 ${(task.requiredDays || []).length} 个打卡日，完成后结算一次`,
    }[task.type] || "";

  return (
    <ModalShell
      title={task.title}
      subtitle="投资任务"
      onClose={onClose}
      maxH="90vh"
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <TypeTag type={task.type} />
        <Pill color={UI.primary} bg={UI.primarySoft}>{`年化 ${(task.yieldRate * 100).toFixed(0)}%`}</Pill>
      </div>

      {task.desc && (
        <div className="text-sm mb-3" style={{ color: UI.muted }}>
          {task.desc}
        </div>
      )}

      <Card className="p-3 mb-4" style={{ boxShadow: "none" }}>
        <div className="text-sm font-semibold" style={{ color: UI.text }}>
          投入金额
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div
            className="px-3 py-2 rounded-xl text-sm font-semibold"
            style={{ background: "rgba(15,23,42,.04)", border: `1px solid ${UI.border}` }}
          >
            ¥
          </div>
          <Input
            inputMode="decimal"
            type="number"
            placeholder={`可用余额 ¥${walletBalance.toFixed(2)}`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{
              borderColor: !amount ? UI.border : validAmt ? "rgba(79,70,229,.35)" : "rgba(220,38,38,.25)",
            }}
          />
        </div>
        <div className="mt-2 text-xs" style={{ color: validAmt ? UI.muted : UI.danger }}>
          {amount && !validAmt
            ? num > walletBalance
              ? "余额不足"
              : "请输入大于 0 的金额"
            : typeInfo}
        </div>
      </Card>

      {isCycle && validAmt && (
        <Card className="p-3 mb-4" style={{ boxShadow: "none" }}>
          <div className="text-sm font-semibold" style={{ color: UI.text }}>
            投资周期数
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Input
              inputMode="numeric"
              type="number"
              placeholder="输入周期数量"
              value={cycles}
              onChange={(e) => setCycles(e.target.value)}
              style={{
                borderColor: !cycles ? UI.border : validCycles ? "rgba(79,70,229,.35)" : "rgba(220,38,38,.25)",
              }}
            />
            <div
              className="px-3 py-2 rounded-xl text-sm font-semibold"
              style={{ background: "rgba(15,23,42,.04)", border: `1px solid ${UI.border}` }}
            >
              个{cycleUnit}
            </div>
          </div>
          <div className="mt-2 text-xs" style={{ color: validCycles ? UI.muted : UI.danger }}>
            {cycles && !validCycles ? "请输入大于 0 的周期数" : `每个${cycleUnit}期完成后结算一次收益`}
          </div>
        </Card>
      )}

      {validAmt && (
        <Card className="p-3 mb-4" style={{ boxShadow: "none" }}>
          <div className="text-xs font-semibold" style={{ color: UI.muted }}>
            预期到期
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <div className="rounded-xl p-2" style={{ background: "rgba(15,23,42,.03)", border: `1px solid ${UI.border}` }}>
              <div className="text-xs" style={{ color: UI.muted }}>本金</div>
              <div className="text-sm font-bold" style={{ color: UI.text }}>¥{formatMoney(num)}</div>
            </div>
            <div className="rounded-xl p-2" style={{ background: "rgba(22,163,74,.06)", border: "1px solid rgba(22,163,74,.12)" }}>
              <div className="text-xs" style={{ color: UI.muted }}>收益</div>
              <div className="text-sm font-bold" style={{ color: UI.success }}>+¥{expectedReturn.toFixed(2)}</div>
            </div>
            <div className="rounded-xl p-2" style={{ background: "rgba(79,70,229,.06)", border: "1px solid rgba(79,70,229,.12)" }}>
              <div className="text-xs" style={{ color: UI.muted }}>合计</div>
              <div className="text-sm font-bold" style={{ color: UI.primary }}>¥{formatMoney(totalExpected)}</div>
            </div>
          </div>
        </Card>
      )}

      <div className="flex gap-2">
        <Button variant="ghost" className="flex-1" onClick={onClose}>
          取消
        </Button>
        <Button
          className="flex-1"
          disabled={!validAmt || (isCycle && !validCycles)}
          onClick={() => validAmt && onAccept(task.id, num, isCycle ? cyclesNum : 1)}
        >
          确认投入
        </Button>
      </div>
    </ModalShell>
  );
}

/* ═══════════════════════════════════════════════
   PROOF UPLOAD MODAL
═══════════════════════════════════════════════ */
function ProofModal({ task, onClose, onSubmit }) {
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState("");
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => setPreview(ev.target.result);
    r.readAsDataURL(f);
  };

  const isCycle = task.type === "cycle_week" || task.type === "cycle_month";
  // 计算当前周期的打卡数
  const currentCycleIndex = (task.completedCycles || 0) + 1;
  const currentCycleCheckins = isCycle
    ? (task.cycleCheckins || []).filter(c => c.cycleIndex === currentCycleIndex)
    : [];
  const cycleLabel = isCycle
    ? `（第 ${currentCycleCheckins.length + 1} 次打卡）`
    : "";

  return (
    <ModalShell title={task.title} subtitle={`提交凭证 ${cycleLabel}`} onClose={onClose} maxH="88vh">
      <div
        className="rounded-2xl overflow-hidden mb-3 flex items-center justify-center cursor-pointer"
        style={{
          height: 190,
          background: preview ? "transparent" : "rgba(15,23,42,.03)",
          border: `1px dashed ${UI.borderStrong}`,
        }}
        onClick={() => fileRef.current?.click()}
      >
        {preview ? (
          <img src={preview} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="text-center px-4">
            <div className="text-sm font-semibold" style={{ color: UI.text }}>
              点击拍照 / 从相册选择
            </div>
            <div className="text-xs mt-1" style={{ color: UI.muted }}>
              上传完成任务的照片凭证
            </div>
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
      />

      {preview && (
        <Button
          variant="subtle"
          className="w-full mb-3"
          onClick={() => {
            setPreview(null);
            if (fileRef.current) fileRef.current.value = "";
          }}
        >
          重新选择
        </Button>
      )}

      <Textarea
        placeholder="备注（可选）"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        className="mb-4"
      />

      <div className="flex gap-2">
        <Button variant="ghost" className="flex-1" onClick={onClose}>
          取消
        </Button>
        <Button
          className="flex-1"
          disabled={!preview}
          onClick={() => preview && onSubmit(task.id, preview, note)}
        >
          提交审核
        </Button>
      </div>
    </ModalShell>
  );
}

/* ═══════════════════════════════════════════════
   CREATE TASK MODAL (parent)
═══════════════════════════════════════════════ */
function CreateTaskModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    title: "",
    desc: "",
    type: "short",
    yieldRate: "15",
    totalDays: "7",
    selectedDays: [],
    minCreditScore: "0",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDay = (d) => {
    set(
      "selectedDays",
      form.selectedDays.includes(d)
        ? form.selectedDays.filter((x) => x !== d)
        : [...form.selectedDays, d]
    );
  };

  const isWeekCycle = form.type === "cycle_week";
  const isMonthCycle = form.type === "cycle_month";
  const isCycle = isWeekCycle || isMonthCycle;

  const valid =
    form.title.trim() &&
    Number(form.yieldRate) > 0 &&
    (form.type === "short" ||
      (form.type === "long" && Number(form.totalDays) > 0) ||
      (isCycle && form.selectedDays.length > 0));

  const handleCreate = () => {
    if (!valid) return;
    const base = {
      id: Date.now(),
      title: form.title.trim(),
      desc: form.desc.trim(),
      type: form.type,
      yieldRate: Number(form.yieldRate) / 100,
      minCreditScore: Number(form.minCreditScore) || 0,
      completed: false,
      reviewStatus: null,
      proof: null,
      investment: null,
    };
    let task;
    if (form.type === "long") task = { ...base, totalDays: Number(form.totalDays), daysCompleted: 0 };
    else if (isCycle)
      task = {
        ...base,
        requiredDays: form.selectedDays,
        cycleCheckins: [],
        cyclesCompleted: 0,
      };
    else task = base;

    onCreate(task);
  };

  const TYPES = [
    { v: "short", label: "单次" },
    { v: "long", label: "连续" },
    { v: "cycle_week", label: "周期·周" },
    { v: "cycle_month", label: "周期·月" },
  ];

  return (
    <ModalShell title="创建任务" subtitle="家长端" onClose={onClose} maxH="92vh">
      <div className="grid grid-cols-4 gap-2 mb-4">
        {TYPES.map(({ v, label }) => (
          <Button
            key={v}
            variant={form.type === v ? "primary" : "subtle"}
            className="w-full py-2"
            onClick={() => set("type", v)}
          >
            {label}
          </Button>
        ))}
      </div>

      <div className="space-y-3">
        <div>
          <div className="text-xs font-semibold mb-1" style={{ color: UI.muted }}>
            任务名称 *
          </div>
          <Input
            placeholder="例如：完成一篇作文"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </div>

        <div>
          <div className="text-xs font-semibold mb-1" style={{ color: UI.muted }}>
            任务描述
          </div>
          <Input
            placeholder="说明具体要求（可选）"
            value={form.desc}
            onChange={(e) => set("desc", e.target.value)}
          />
        </div>

        <div>
          <div className="text-xs font-semibold mb-1" style={{ color: UI.muted }}>
            收益率（%）*
          </div>
          <div className="flex gap-2 items-center">
            <Input
              type="number"
              inputMode="numeric"
              value={form.yieldRate}
              onChange={(e) => set("yieldRate", e.target.value)}
              className="flex-1"
            />
            <div className="flex gap-1">
              {[5, 10, 15, 20].map((v) => (
                <Button
                  key={v}
                  variant={String(v) === form.yieldRate ? "primary" : "subtle"}
                  className="px-2 py-2"
                  onClick={() => set("yieldRate", String(v))}
                >
                  {v}%
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold mb-1" style={{ color: UI.muted }}>
            最低信用分要求
          </div>
          <Input
            type="number"
            inputMode="numeric"
            value={form.minCreditScore}
            onChange={(e) => set("minCreditScore", e.target.value)}
            placeholder="0（不限制），范围 0-1000"
          />
          <div className="text-xs mt-1" style={{ color: UI.muted }}>
            信用分达到要求才能投资此任务
          </div>
        </div>

        {form.type === "long" && (
          <div>
            <div className="text-xs font-semibold mb-1" style={{ color: UI.muted }}>
              连续打卡天数 *
            </div>
            <Input
              type="number"
              value={form.totalDays}
              onChange={(e) => set("totalDays", e.target.value)}
            />
          </div>
        )}

        {isCycle && (
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color: UI.muted }}>
              选择{isWeekCycle ? "每周" : "每月"}打卡日 *（已选 {form.selectedDays.length}）
            </div>

            {isWeekCycle ? (
              <div className="grid grid-cols-7 gap-1.5">
                {WEEKDAYS.map((l, i) => {
                  const active = form.selectedDays.includes(i);
                  return (
                    <button
                      key={i}
                      onClick={() => toggleDay(i)}
                      className="py-2 rounded-xl text-xs font-semibold"
                      style={{
                        background: active ? UI.primarySoft : "rgba(15,23,42,.03)",
                        border: `1px solid ${active ? "rgba(79,70,229,.25)" : UI.border}`,
                        color: active ? UI.primary : UI.text,
                      }}
                    >
                      周{l}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                  const active = form.selectedDays.includes(d);
                  return (
                    <button
                      key={d}
                      onClick={() => toggleDay(d)}
                      className="aspect-square rounded-lg text-xs font-semibold flex items-center justify-center"
                      style={{
                        background: active ? "rgba(219,39,119,.10)" : "rgba(15,23,42,.03)",
                        border: `1px solid ${active ? "rgba(219,39,119,.20)" : UI.border}`,
                        color: active ? "#db2777" : UI.text,
                      }}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-5">
        <Button variant="ghost" className="flex-1" onClick={onClose}>
          取消
        </Button>
        <Button className="flex-1" disabled={!valid} onClick={handleCreate}>
          发布任务
        </Button>
      </div>
    </ModalShell>
  );
}

/* ═══════════════════════════════════════════════
   EDIT TASK MODAL
═══════════════════════════════════════════════ */
function EditTaskModal({ task, onClose, onSave }) {
  const [form, setForm] = useState({
    title: task.title,
    desc: task.desc || "",
    type: task.type,
    yieldRate: String((task.yieldRate * 100).toFixed(0)),
    totalDays: task.totalDays || "7",
    selectedDays: task.requiredDays || [],
    minCreditScore: String(task.minCreditScore || 0),
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDay = (d) => {
    set(
      "selectedDays",
      form.selectedDays.includes(d)
        ? form.selectedDays.filter((x) => x !== d)
        : [...form.selectedDays, d]
    );
  };

  const isWeekCycle = form.type === "cycle_week";
  const isMonthCycle = form.type === "cycle_month";
  const isCycle = isWeekCycle || isMonthCycle;

  const valid =
    form.title.trim() &&
    Number(form.yieldRate) > 0 &&
    (form.type === "short" ||
      (form.type === "long" && Number(form.totalDays) > 0) ||
      (isCycle && form.selectedDays.length > 0));

  const handleSave = () => {
    if (!valid) return;
    const base = {
      id: task.id,
      title: form.title.trim(),
      desc: form.desc.trim(),
      type: form.type,
      yieldRate: Number(form.yieldRate) / 100,
      minCreditScore: Number(form.minCreditScore) || 0,
      completed: task.completed,
      reviewStatus: task.reviewStatus,
      proof: task.proof,
      investment: task.investment,
      daysCompleted: task.daysCompleted,
      cycleCheckins: task.cycleCheckins,
      cyclesCompleted: task.cyclesCompleted,
      accumulatedEarnings: task.accumulatedEarnings,
    };
    let updatedTask;
    if (form.type === "long") updatedTask = { ...base, totalDays: Number(form.totalDays) };
    else if (isCycle)
      updatedTask = {
        ...base,
        requiredDays: form.selectedDays,
      };
    else updatedTask = base;

    onSave(updatedTask);
  };

  const TYPES = [
    { v: "short", label: "单次" },
    { v: "long", label: "连续" },
    { v: "cycle_week", label: "周期·周" },
    { v: "cycle_month", label: "周期·月" },
  ];

  return (
    <ModalShell title="编辑任务" subtitle="家长端" onClose={onClose} maxH="92vh">
      <div className="grid grid-cols-4 gap-2 mb-4">
        {TYPES.map(({ v, label }) => (
          <Button
            key={v}
            variant={form.type === v ? "primary" : "subtle"}
            className="w-full py-2"
            onClick={() => set("type", v)}
            disabled={isCycle && task.cycleCheckins && task.cycleCheckins.length > 0}
          >
            {label}
          </Button>
        ))}
      </div>

      <div className="space-y-3">
        <div>
          <div className="text-xs font-semibold mb-1" style={{ color: UI.muted }}>
            任务名称 *
          </div>
          <Input
            placeholder="例如：完成一篇作文"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </div>

        <div>
          <div className="text-xs font-semibold mb-1" style={{ color: UI.muted }}>
            任务描述
          </div>
          <Input
            placeholder="说明具体要求（可选）"
            value={form.desc}
            onChange={(e) => set("desc", e.target.value)}
          />
        </div>

        <div>
          <div className="text-xs font-semibold mb-1" style={{ color: UI.muted }}>
            收益率（%）*
          </div>
          <div className="flex gap-2 items-center">
            <Input
              type="number"
              inputMode="numeric"
              value={form.yieldRate}
              onChange={(e) => set("yieldRate", e.target.value)}
              className="flex-1"
            />
            <div className="flex gap-1">
              {[5, 10, 15, 20].map((v) => (
                <Button
                  key={v}
                  variant={String(v) === form.yieldRate ? "primary" : "subtle"}
                  className="px-2 py-2"
                  onClick={() => set("yieldRate", String(v))}
                >
                  {v}%
                </Button>
              ))}
            </div>
          </div>
        </div>

        {form.type === "long" && (
          <div>
            <div className="text-xs font-semibold mb-1" style={{ color: UI.muted }}>
              连续打卡天数 *
            </div>
            <Input
              type="number"
              value={form.totalDays}
              onChange={(e) => set("totalDays", e.target.value)}
            />
          </div>
        )}

        {isCycle && (
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color: UI.muted }}>
              选择{isWeekCycle ? "每周" : "每月"}打卡日 *（已选 {form.selectedDays.length}）
            </div>

            {isWeekCycle ? (
              <div className="grid grid-cols-7 gap-1.5">
                {WEEKDAYS.map((l, i) => {
                  const active = form.selectedDays.includes(i);
                  return (
                    <button
                      key={i}
                      onClick={() => toggleDay(i)}
                      className="py-2 rounded-xl text-xs font-semibold"
                      style={{
                        background: active ? UI.primarySoft : "rgba(15,23,42,.03)",
                        border: `1px solid ${active ? "rgba(79,70,229,.25)" : UI.border}`,
                        color: active ? UI.primary : UI.text,
                      }}
                    >
                      周{l}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                  const active = form.selectedDays.includes(d);
                  return (
                    <button
                      key={d}
                      onClick={() => toggleDay(d)}
                      className="aspect-square rounded-lg text-xs font-semibold flex items-center justify-center"
                      style={{
                        background: active ? "rgba(219,39,119,.10)" : "rgba(15,23,42,.03)",
                        border: `1px solid ${active ? "rgba(219,39,119,.20)" : UI.border}`,
                        color: active ? "#db2777" : UI.text,
                      }}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-5">
        <Button variant="ghost" className="flex-1" onClick={onClose}>
          取消
        </Button>
        <Button className="flex-1" disabled={!valid} onClick={handleSave}>
          保存修改
        </Button>
      </div>
    </ModalShell>
  );
}

/* ═══════════════════════════════════════════════
   OPTIMIZED TASK CARD (child)
═══════════════════════════════════════════════ */
function OptimizedTaskCard({ task, creditScore, onAccept, onSubmitProof, onEarlyWithdraw }) {
  const [expanded, setExpanded] = useState(false);
  const notAccepted = task.investment === null || task.investment === undefined;
  const isPending = task.reviewStatus === "pending";
  const isApproved = task.reviewStatus === "approved" && task.completed;
  const isRejected = task.reviewStatus === "rejected";
  const isCycle = task.type === "cycle_week" || task.type === "cycle_month";
  const isLong = task.type === "long";
  const minCreditScore = task.minCreditScore || 0;
  const canInvest = minCreditScore === 0 || creditScore >= minCreditScore;

  const expectedReturn = task.investment ? calcReturn(task.investment, task.yieldRate) : null;

  // 获取状态颜色和标签
  const getStatusInfo = () => {
    if (isApproved) return { color: UI.success, bg: "rgba(22,163,74,.10)", label: "已完成", icon: <IconCheck className="w-3.5 h-3.5" /> };
    if (isPending) return { color: UI.warn, bg: "rgba(217,119,6,.10)", label: "审核中", icon: <IconRefresh className="w-3.5 h-3.5" /> };
    if (isRejected) return { color: UI.danger, bg: "rgba(220,38,38,.10)", label: "已拒绝", icon: <IconX className="w-3.5 h-3.5" /> };
    if (notAccepted) return { color: UI.muted, bg: "rgba(15,23,42,.04)", label: "待接取", icon: <IconCircle className="w-3.5 h-3.5" /> };
    return { color: UI.primary, bg: UI.primarySoft, label: "进行中", icon: <IconDot className="w-3.5 h-3.5" /> };
  };

  const statusInfo = getStatusInfo();

  // 获取进度信息
  const getProgressInfo = () => {
    if (isCycle && task.investment) {
      const completedCycles = task.completedCycles || 0;
      const totalCycles = task.cycles || 1;
      const progress = Math.round((completedCycles / totalCycles) * 100);
      return { text: `${completedCycles}/${totalCycles} 周期`, progress };
    }
    if (isLong && task.investment) {
      const daysCompleted = task.daysCompleted || 0;
      const totalDays = task.totalDays || 7;
      const progress = Math.round((daysCompleted / totalDays) * 100);
      return { text: `${daysCompleted}/${totalDays} 天`, progress };
    }
    return null;
  };

  const progressInfo = getProgressInfo();

  return (
    <Card
      className="overflow-hidden transition-all duration-200"
      style={{
        boxShadow: "0 2px 8px rgba(15,23,42,.06)",
        borderLeft: `3px solid ${statusInfo.color}`,
      }}
    >
      {/* 头部 - 始终显示 */}
      <div
        className="p-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* 类型标签和状态 */}
            <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
              <TypeTag type={task.type} />
              <span
                className="text-xs px-1 py-0.5 rounded-full font-medium flex items-center gap-1"
                style={{ background: statusInfo.bg, color: statusInfo.color }}
              >
                {statusInfo.icon}
                <span>{statusInfo.label}</span>
              </span>
              <span
                className="text-xs px-1 py-0.5 rounded-full"
                style={{ background: "rgba(15,23,42,.04)", color: UI.muted }}
              >
                {(task.yieldRate * 100).toFixed(0)}%
              </span>
            </div>

            {/* 标题 */}
            <div className="text-sm font-bold truncate" style={{ color: UI.text }}>
              {task.title}
            </div>

            {/* 进度条（紧凑模式） */}
            {progressInfo && (
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1" style={{ color: UI.muted }}>
                  <span>进度</span>
                  <span>{progressInfo.text}</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(15,23,42,.06)" }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${progressInfo.progress}%`,
                      background: `linear-gradient(90deg, ${statusInfo.color}80 0%, ${statusInfo.color} 100%)`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* 右侧投资和收益 */}
          {task.investment ? (
            <div className="shrink-0 text-right">
              <div className="text-xs" style={{ color: UI.muted }}>投入</div>
              <div className="text-sm font-bold" style={{ color: UI.text }}>
                ¥{formatMoney(task.investment)}
              </div>
              <div className="text-xs font-medium" style={{ color: UI.success }}>
                +¥{formatMoney(expectedReturn)}
              </div>
            </div>
          ) : (
            <div className="shrink-0 text-right">
              <div className="text-xs" style={{ color: UI.muted }}>预计收益</div>
              <div className="text-sm font-bold" style={{ color: UI.success }}>
                {(task.yieldRate * 100).toFixed(0)}%
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 展开内容 */}
      {expanded && (
        <div className="px-3 pb-3 border-t" style={{ borderColor: UI.border }}>
          {/* 描述 */}
          {task.desc && (
            <div className="pt-2 pb-2 text-xs leading-5" style={{ color: UI.muted }}>
              {task.desc}
            </div>
          )}

          {/* 信用分要求 */}
          {minCreditScore > 0 && (
            <div className="py-2 text-xs" style={{ color: creditScore >= minCreditScore ? UI.success : UI.warn }}>
              信用分要求: {minCreditScore} {creditScore >= minCreditScore ? "(已满足)" : `(当前 ${creditScore})`}
            </div>
          )}

          {/* 操作按钮 */}
          <div className="pt-2">
            {notAccepted ? (
              canInvest ? (
                <Button
                  className="w-full"
                  onClick={() => onAccept(task)}
                  style={{
                    background: UI.accentGreenSoft,
                    border: `1px solid rgba(34,197,94,.28)`,
                    color: "#166534",
                  }}
                >
                  投资并开始
                </Button>
              ) : (
                <Button className="w-full" disabled style={{ opacity: 0.5 }}>
                  信用分不足 ({creditScore}/{minCreditScore})
                </Button>
              )
            ) : !isApproved && !isPending ? (
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={() => onSubmitProof(task)}
                  style={{
                    background: isRejected ? "rgba(217,119,6,.12)" : UI.accentCyanSoft,
                    border: `1px solid ${isRejected ? "rgba(217,119,6,.22)" : "rgba(6,182,212,.28)"}`,
                    color: isRejected ? UI.warn : "#0e7490",
                  }}
                >
                  {isRejected ? "重新提交" : "提交打卡"}
                </Button>
                {(isLong || isCycle) && (
                  <Button variant="danger" onClick={() => onEarlyWithdraw(task.id)}>
                    退出
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-2 text-xs" style={{ color: UI.muted }}>
                {isPending ? "等待家长审核中..." : "任务已完成"}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════
   TASK CARD (child) - 保留原组件用于其他页面
═══════════════════════════════════════════════ */
function TaskCard({ task, creditScore, onAccept, onSubmitProof, onEarlyWithdraw }) {
  const notAccepted = task.investment === null || task.investment === undefined;
  const isPending = task.reviewStatus === "pending";
  const isApproved = task.reviewStatus === "approved" && task.completed;
  const isRejected = task.reviewStatus === "rejected";
  const isCycle = task.type === "cycle_week" || task.type === "cycle_month";
  const minCreditScore = task.minCreditScore || 0;
  const canInvest = minCreditScore === 0 || creditScore >= minCreditScore;
  
  const canCheckInToday = () => {
    if (!isCycle) return true;
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    const currentDayOfWeek = today.getDay();
    const currentDayOfMonth = today.getDate();

    // 只检查当前周期的打卡记录
    const currentCycleIndex = (task.completedCycles || 0) + 1;
    const currentCycleCheckins = (task.cycleCheckins || []).filter(c => c.cycleIndex === currentCycleIndex);
    const alreadyCheckedIn = currentCycleCheckins.some(c => c.date === todayStr);
    if (alreadyCheckedIn) return false;

    if (task.type === "cycle_week") {
      return task.requiredDays?.includes(currentDayOfWeek);
    } else if (task.type === "cycle_month") {
      return task.requiredDays?.includes(currentDayOfMonth);
    }
    return false;
  };
  
  const isCheckInDay = canCheckInToday();

  const expectedReturn = task.investment ? calcReturn(task.investment, task.yieldRate) : null;

  const allCycleCheckins = task.cycleCheckins || [];
  const requiredDays = task.requiredDays || [];
  const totalCycles = task.cycles || 1;
  const completedCycles = task.completedCycles || 0;
  // 只计算当前周期的打卡进度
  const currentCycleIndex = completedCycles + 1;
  const currentCycleCheckins = allCycleCheckins.filter(c => c.cycleIndex === currentCycleIndex);
  const cycleProgress =
    requiredDays.length > 0 ? Math.round((currentCycleCheckins.length / requiredDays.length) * 100) : 0;
  const totalCycleProgress = totalCycles > 0 ? Math.round((completedCycles / totalCycles) * 100) : 0;
  
  const getCheckinStatus = (day) => {
    // 只检查当前周期的打卡记录
    const currentCycleIndex = (task.completedCycles || 0) + 1;
    const currentCycleCheckins = allCycleCheckins.filter(c => c.cycleIndex === currentCycleIndex);

    if (task.type === "cycle_week") {
      const today = new Date();
      const currentWeekDay = today.getDay();
      if (day === currentWeekDay) {
        const todayStr = today.toISOString().slice(0, 10);
        return currentCycleCheckins.some(c => c.date === todayStr);
      }
      return false;
    } else if (task.type === "cycle_month") {
      const today = new Date();
      const currentMonthDay = today.getDate();
      if (day === currentMonthDay) {
        const todayStr = today.toISOString().slice(0, 10);
        return currentCycleCheckins.some(c => c.date === todayStr);
      }
      return false;
    }
    return false;
  };

  const borderColor = notAccepted
    ? UI.border
    : isPending
    ? "rgba(217,119,6,.22)"
    : isApproved
    ? "rgba(22,163,74,.18)"
    : isRejected
    ? "rgba(220,38,38,.18)"
    : UI.border;

  return (
    <Card className="p-4 mb-3" style={{ borderColor, boxShadow: "0 10px 24px rgba(15,23,42,.05)" }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <TypeTag type={task.type} />
            <Pill color={UI.muted} bg="rgba(15,23,42,.04)">{`${(task.yieldRate * 100).toFixed(0)}%`}</Pill>
            {task.reviewStatus && <StatusPill status={task.reviewStatus} />}
          </div>

          <div className="mt-2 text-sm font-bold" style={{ color: UI.text }}>
            {task.title}
          </div>
          {task.desc && (
            <div className="mt-1 text-xs leading-5" style={{ color: UI.muted }}>
              {task.desc}
            </div>
          )}

          {isCycle && task.investment && (
            <div className="mt-3">
              <div className="text-xs font-semibold mb-2" style={{ color: UI.text }}>
                总周期进度 {completedCycles}/{totalCycles}
              </div>
              <div className="flex justify-between text-xs mb-1" style={{ color: UI.muted }}>
                <span>总完成度</span>
                <span>{totalCycleProgress}%</span>
              </div>
              <div className="mt-1 h-2 rounded-full overflow-hidden" style={{ background: "rgba(15,23,42,.06)" }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${totalCycleProgress}%`, background: "linear-gradient(90deg, rgba(79,70,229,.75) 0%, rgba(217,119,6,.75) 100%)" }}
                />
              </div>

              <div className="flex justify-between text-xs mt-3 mb-1" style={{ color: UI.muted }}>
                <span>
                  本周期 {currentCycleCheckins.length}/{requiredDays.length}
                </span>
                <span>{cycleProgress}%</span>
              </div>
              <div className="mt-1 h-2 rounded-full overflow-hidden" style={{ background: "rgba(15,23,42,.06)" }}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${cycleProgress}%`, background: "rgba(217,119,6,.75)" }}
                />
              </div>
              <div className="flex gap-1 mt-2 flex-wrap">
                {requiredDays
                  .slice()
                  .sort((a, b) => a - b)
                  .map((d) => {
                    const done = getCheckinStatus(d);
                    return (
                      <span
                        key={d}
                        className="text-xs px-2 py-0.5 rounded-lg font-semibold"
                        style={{
                          background: done ? "rgba(217,119,6,.10)" : "rgba(15,23,42,.03)",
                          color: done ? UI.warn : UI.muted,
                          border: `1px solid ${done ? "rgba(217,119,6,.16)" : UI.border}`,
                        }}
                      >
                        {task.type === "cycle_week" ? `周${WEEKDAYS[d]}` : `${d}日`}
                      </span>
                    );
                  })}
              </div>
            </div>
          )}

          {task.type === "long" && task.investment && (
            <div className="mt-3">
              <div className="flex justify-between text-xs" style={{ color: UI.muted }}>
                <span>
                  进度 {task.daysCompleted || 0}/{task.totalDays} 天
                </span>
                <span>{Math.round(((task.daysCompleted || 0) / task.totalDays) * 100)}%</span>
              </div>
              <div className="mt-1 h-2 rounded-full overflow-hidden" style={{ background: "rgba(15,23,42,.06)" }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.round(((task.daysCompleted || 0) / task.totalDays) * 100)}%`,
                    background: "rgba(79,70,229,.75)",
                  }}
                />
              </div>
            </div>
          )}

          {task.proof && (
            <div className="mt-3 rounded-xl overflow-hidden" style={{ height: 70, border: `1px solid ${UI.border}` }}>
              <img src={task.proof} alt="proof" className="w-full h-full object-cover" />
            </div>
          )}

          {isRejected && task.rejectReason && (
            <div className="mt-3 p-3 rounded-xl" style={{ background: "rgba(220,38,38,.08)", border: `1px solid rgba(220,38,38,.20)` }}>
              <div className="text-xs font-semibold mb-1" style={{ color: "#dc2626" }}>
                拒绝原因
              </div>
              <div className="text-xs" style={{ color: "#991b1b" }}>
                {task.rejectReason}
              </div>
            </div>
          )}
        </div>

        {task.investment && (
          <div className="shrink-0 text-right">
            <div className="text-xs" style={{ color: UI.muted }}>
              投入
            </div>
            <div className="text-base font-bold" style={{ color: UI.text }}>
              ¥{formatMoney(task.investment)}
            </div>
            <div className="text-xs font-semibold" style={{ color: UI.success }}>
              +¥{formatMoney(expectedReturn)}
            </div>
          </div>
        )}
      </div>

      <div className="mt-3">
        {notAccepted ? (
          canInvest ? (
            <Button
              className="w-full"
              onClick={() => onAccept(task)}
              style={{
                background: UI.accentGreenSoft,
                border: `1px solid rgba(34,197,94,.28)`,
                color: "#166534",
              }}
            >
              投资并开始
            </Button>
          ) : (
            <Button
              className="w-full"
              disabled
              style={{
                background: "rgba(15,23,42,.06)",
                border: `1px solid ${UI.border}`,
                color: UI.muted,
              }}
            >
              信用分 {creditScore} 不足（需 {minCreditScore}）
            </Button>
          )
        ) : !isApproved && !isPending ? (
          <div className="flex gap-2">
            <Button
              className="flex-1"
              disabled={!isCheckInDay && !isRejected}
              onClick={() => onSubmitProof(task)}
              style={
                isRejected
                  ? { background: "rgba(217,119,6,.12)", border: "1px solid rgba(217,119,6,.22)", color: UI.warn }
                  : !isCheckInDay
                  ? { background: "rgba(15,23,42,.06)", border: `1px solid ${UI.border}`, color: UI.muted }
                  : {
                      background: UI.accentCyanSoft,
                      border: "1px solid rgba(6,182,212,.28)",
                      color: "#0e7490",
                    }
              }
            >
              {isRejected ? "重新提交" : !isCheckInDay ? "非打卡日" : "提交打卡"}
            </Button>
            {(task.type === "long" || isCycle) &&
              ((task.daysCompleted || 0) > 0 || (task.cycleCheckins || []).length > 0) && (
                <Button variant="danger" onClick={() => onEarlyWithdraw(task.id)}>
                  提前退出
                </Button>
              )}
          </div>
        ) : isApproved ? (
          <div className="text-xs font-semibold text-center" style={{ color: UI.success }}>
            已完成 · 收益到账
          </div>
        ) : (
          <div className="text-xs font-semibold text-center" style={{ color: UI.warn }}>
            等待审核…
          </div>
        )}
      </div>
    </Card>
  );
}

/* ═══════════════════════════════════════════════
   PARENT STATS PANEL (统计面板)
═══════════════════════════════════════════════ */
function ParentStatsPanel({ submissions, tasks, wallet, savings, creditScore }) {
  const pendingCount = submissions.filter(s => s.status === "pending").length;
  const approvedCount = submissions.filter(s => s.status === "approved").length;
  const totalTasks = tasks.length;
  const activeTasks = tasks.filter(t => t.investment && !t.completed).length;
  const completedTasks = tasks.filter(t => t.completed).length;
  const totalInvestment = tasks.filter(t => t.investment).reduce((sum, t) => sum + t.investment, 0);
  const totalAssets = wallet + savings.balance + totalInvestment;

  const stats = [
    {
      label: "待审核",
      value: pendingCount,
      icon: <IconRefresh className="w-5 h-5" />,
      color: UI.warn,
      bg: "rgba(217,119,6,.10)",
      desc: "需要处理"
    },
    {
      label: "已通过",
      value: approvedCount,
      icon: <IconCheck className="w-5 h-5" />,
      color: UI.success,
      bg: "rgba(22,163,74,.10)",
      desc: "审核完成"
    },
    {
      label: "总任务",
      value: totalTasks,
      icon: <IconClipboard className="w-5 h-5" />,
      color: UI.primary,
      bg: UI.primarySoft,
      desc: `${activeTasks}进行中 ${completedTasks}已完成`
    },
    {
      label: "总投资",
      value: `¥${formatMoney(totalInvestment)}`,
      icon: <IconMoney className="w-5 h-5" />,
      color: "#f59e0b",
      bg: "rgba(245,158,11,.10)",
      desc: "任务投资"
    },
  ];

  return (
    <Card className="p-4 mb-4" style={{ boxShadow: "0 2px 12px rgba(15,23,42,.06)" }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-bold" style={{ color: UI.text }}>
          <IconChart className="w-4 h-4" />
          数据概览
        </div>
        <div className="text-xs" style={{ color: UI.muted }}>
          总资产 ¥{formatMoney(totalAssets)}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="p-3 rounded-xl"
            style={{ background: stat.bg, border: `1px solid ${stat.color}20` }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{stat.icon}</span>
              <span className="text-xs font-medium" style={{ color: stat.color }}>
                {stat.label}
              </span>
            </div>
            <div className="text-xl font-bold" style={{ color: UI.text }}>
              {stat.value}
            </div>
            <div className="text-xs mt-0.5" style={{ color: UI.muted }}>
              {stat.desc}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ═══════════════════════════════════════════════
   TASK MANAGER CARD (任务管理卡片)
═══════════════════════════════════════════════ */
function TaskManagerCard({ task, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const notStarted = !task.investment && !task.completed;
  const inProgress = task.investment && !task.completed && !task.reviewStatus;
  const isCompleted = task.completed || task.reviewStatus === "approved";

  const getStatusInfo = () => {
    if (isCompleted) return { color: UI.success, bg: "rgba(22,163,74,.10)", label: "已完成", icon: "✓" };
    if (task.reviewStatus === "pending") return { color: UI.warn, bg: "rgba(217,119,6,.10)", label: "审核中", icon: "⏳" };
    if (inProgress) return { color: UI.primary, bg: UI.primarySoft, label: "进行中", icon: "●" };
    return { color: UI.muted, bg: "rgba(15,23,42,.04)", label: "待开始", icon: "○" };
  };

  const statusInfo = getStatusInfo();

  // 获取进度信息
  const getProgressInfo = () => {
    if (task.type === "cycle_week" || task.type === "cycle_month") {
      const completed = task.cyclesCompleted || 0;
      const total = task.cycles || 1;
      return { text: `${completed}/${total} 周期`, percent: Math.round((completed / total) * 100) };
    }
    if (task.type === "long") {
      const completed = task.daysCompleted || 0;
      const total = task.totalDays || 7;
      return { text: `${completed}/${total} 天`, percent: Math.round((completed / total) * 100) };
    }
    return null;
  };

  const progressInfo = getProgressInfo();

  return (
    <Card
      className="overflow-hidden transition-all duration-200 mb-2"
      style={{ 
        boxShadow: "0 2px 8px rgba(15,23,42,.04)",
        borderLeft: `3px solid ${statusInfo.color}` 
      }}
    >
      {/* 头部 */}
      <div 
        className="p-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <TypeTag type={task.type} />
              <span
                className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                style={{ background: statusInfo.bg, color: statusInfo.color }}
              >
                {statusInfo.icon} {statusInfo.label}
              </span>
              <span
                className="text-xs px-1.5 py-0.5 rounded-full"
                style={{ background: "rgba(15,23,42,.04)", color: UI.muted }}
              >
                {(task.yieldRate * 100).toFixed(0)}%
              </span>
            </div>
            <div className="text-sm font-bold truncate" style={{ color: UI.text }}>
              {task.title}
            </div>
            {progressInfo && (
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1" style={{ color: UI.muted }}>
                  <span>进度</span>
                  <span>{progressInfo.text}</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(15,23,42,.06)" }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${progressInfo.percent}%`,
                      background: `linear-gradient(90deg, ${statusInfo.color}60 0%, ${statusInfo.color} 100%)`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          
          <div className="shrink-0 text-right">
            {task.investment ? (
              <>
                <div className="text-xs" style={{ color: UI.muted }}>投入</div>
                <div className="text-sm font-bold" style={{ color: UI.text }}>
                  ¥{formatMoney(task.investment)}
                </div>
              </>
            ) : (
              <span
                className="text-xs px-2 py-1 rounded-full"
                style={{ background: "rgba(15,23,42,.04)", color: UI.muted }}
              >
                未投入
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 展开内容 */}
      {expanded && (
        <div className="px-3 pb-3 border-t" style={{ borderColor: UI.border }}>
          {task.desc && (
            <div className="pt-2 pb-2 text-xs leading-5" style={{ color: UI.muted }}>
              {task.desc}
            </div>
          )}
          
          <div className="flex items-center gap-4 py-2 text-xs" style={{ color: UI.muted }}>
            {task.minCreditScore > 0 && (
              <span>信用分要求: {task.minCreditScore}</span>
            )}
            {task.totalDays && (
              <span>总天数: {task.totalDays}天</span>
            )}
            {task.cycles && (
              <span>总周期: {task.cycles}个</span>
            )}
          </div>

          {/* 操作按钮 */}
          {notStarted && (
            <div className="flex gap-2 pt-2">
              <Button
                className="flex-1"
                onClick={() => onEdit(task)}
                style={{
                  background: UI.accentCyanSoft,
                  border: `1px solid rgba(6,182,212,.28)`,
                  color: "#0e7490",
                }}
              >
                编辑
              </Button>
              <Button variant="danger" onClick={() => onDelete(task.id)}>
                删除
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════
   PARENT TASK MANAGER (带筛选的任务管理)
═══════════════════════════════════════════════ */
function ParentTaskManager({ tasks, onEdit, onDelete, onCreate }) {
  const [filter, setFilter] = useState("all"); // all, pending, active, completed
  const [sortBy, setSortBy] = useState("default"); // default, yield, investment
  const [expandedTypes, setExpandedTypes] = useState({
    short: true,
    long: true,
    cycle_week: true,
    cycle_month: true,
  });

  const taskTypes = [
    { key: "short", label: "单次任务", icon: <IconZap className="w-4 h-4" /> },
    { key: "long", label: "连续打卡", icon: <IconCalendar className="w-4 h-4" /> },
    { key: "cycle_week", label: "周期·周", icon: <IconRefresh className="w-4 h-4" /> },
    { key: "cycle_month", label: "周期·月", icon: <IconCalendar className="w-4 h-4" /> },
  ];

  const filterOptions = [
    { key: "all", label: "全部", count: tasks.length },
    { key: "pending", label: "待开始", count: tasks.filter(t => !t.investment && !t.completed).length },
    { key: "active", label: "进行中", count: tasks.filter(t => t.investment && !t.completed && !t.reviewStatus).length },
    { key: "completed", label: "已完成", count: tasks.filter(t => t.completed || t.reviewStatus === "approved").length },
  ];

  const getFilteredTasks = (type) => {
    let filtered = tasks.filter((t) => t.type === type);

    // 应用筛选
    switch (filter) {
      case "pending":
        filtered = filtered.filter((t) => !t.investment && !t.completed);
        break;
      case "active":
        filtered = filtered.filter((t) => t.investment && !t.completed && !t.reviewStatus);
        break;
      case "completed":
        filtered = filtered.filter((t) => t.completed || t.reviewStatus === "approved");
        break;
      default:
        break;
    }

    // 应用排序
    switch (sortBy) {
      case "yield":
        filtered = [...filtered].sort((a, b) => b.yieldRate - a.yieldRate);
        break;
      case "investment":
        filtered = [...filtered].sort((a, b) => (b.investment || 0) - (a.investment || 0));
        break;
      default:
        break;
    }

    return filtered;
  };

  const toggleType = (type) => {
    setExpandedTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const filteredCount = taskTypes.reduce((sum, { key }) => sum + getFilteredTasks(key).length, 0);

  return (
    <div className="mb-5">
      {/* 头部 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <IconClipboard className="w-5 h-5" style={{ color: UI.primary }} />
          <span className="text-sm font-bold" style={{ color: UI.text }}>
            任务管理
          </span>
          <span
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ background: "rgba(15,23,42,.06)", color: UI.muted }}
          >
            {filteredCount}
          </span>
        </div>
        <Button
          onClick={onCreate}
          style={{
            background: UI.accentGreenSoft,
            border: `1px solid rgba(34,197,94,.28)`,
            color: "#166534",
          }}
        >
          + 创建任务
        </Button>
      </div>

      {/* 筛选栏 */}
      {tasks.length > 0 && (
        <Card className="p-3 mb-3" style={{ boxShadow: "0 2px 8px rgba(15,23,42,.04)" }}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold" style={{ color: UI.muted }}>
              筛选
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-xs px-2 py-1 rounded-lg border outline-none"
              style={{ borderColor: UI.border, color: UI.text, background: UI.panel }}
            >
              <option value="default">默认排序</option>
              <option value="yield">收益率排序</option>
              <option value="investment">投资额排序</option>
            </select>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {filterOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setFilter(opt.key)}
                className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                style={{
                  background: filter === opt.key ? UI.primarySoft : "rgba(15,23,42,.04)",
                  color: filter === opt.key ? UI.primary : UI.muted,
                  border: `1px solid ${filter === opt.key ? UI.primary : UI.border}`,
                }}
              >
                {opt.label} ({opt.count})
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* 任务列表 */}
      {tasks.length === 0 ? (
        <Card className="p-8 text-center" style={{ boxShadow: "0 2px 8px rgba(15,23,42,.04)" }}>
          <div className="flex justify-center mb-2">
            <IconTarget className="w-12 h-12" style={{ color: UI.primary }} />
          </div>
          <div className="text-sm font-medium" style={{ color: UI.text }}>
            暂无任务
          </div>
          <div className="text-xs mt-1" style={{ color: UI.muted }}>
            点击上方按钮创建第一个任务
          </div>
        </Card>
      ) : filteredCount === 0 ? (
        <Card className="p-8 text-center" style={{ boxShadow: "0 2px 8px rgba(15,23,42,.04)" }}>
          <div className="flex justify-center mb-2">
            <IconSearch className="w-12 h-12" style={{ color: UI.muted }} />
          </div>
          <div className="text-sm font-medium" style={{ color: UI.text }}>
            暂无符合条件的任务
          </div>
          <div className="text-xs mt-1" style={{ color: UI.muted }}>
            尝试切换其他筛选条件
          </div>
        </Card>
      ) : (
        <div>
          {taskTypes.map(({ key, label, icon }) => {
            const list = getFilteredTasks(key);
            if (!list.length) return null;

            return (
              <div key={key} className="mb-3">
                {/* 类型标题 */}
                <button
                  onClick={() => toggleType(key)}
                  className="w-full flex items-center justify-between px-1 py-2 mb-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{icon}</span>
                    <span className="text-sm font-bold" style={{ color: UI.text }}>
                      {label}
                    </span>
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-full"
                      style={{ background: "rgba(15,23,42,.06)", color: UI.muted }}
                    >
                      {list.length}
                    </span>
                  </div>
                  <span
                    className="text-xs transition-transform"
                    style={{
                      color: UI.muted,
                      transform: expandedTypes[key] ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  >
                    ▼
                  </span>
                </button>

                {/* 任务卡片 */}
                {expandedTypes[key] && (
                  <div className="space-y-2">
                    {list.map((t) => (
                      <TaskManagerCard
                        key={t.id}
                        task={t}
                        onEdit={onEdit}
                        onDelete={onDelete}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   REVIEW CARD (parent)
═══════════════════════════════════════════════ */
function ReviewCard({ submission, onApprove, onReject }) {
  const [reason, setReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  return (
    <Card className="p-4 mb-3" style={{ borderColor: "rgba(217,119,6,.20)", boxShadow: "0 10px 24px rgba(15,23,42,.04)" }}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Pill color={UI.warn} bg="rgba(217,119,6,.10)">待审核</Pill>
            <TypeTag type={submission.taskType} />
            {submission.isCycleCheckin && (
              <span className="text-xs" style={{ color: UI.muted }}>
                周期打卡 {submission.checkinIndex}
              </span>
            )}
          </div>
          <div className="mt-2 text-sm font-bold" style={{ color: UI.text }}>
            {submission.taskTitle}
          </div>
          <div className="text-xs mt-1 flex items-center gap-2" style={{ color: UI.muted }}>
            <span>{submission.submittedAt}</span>
            {submission.memberName && (
              <span style={{ color: UI.primary }}>· {submission.memberName}</span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs" style={{ color: UI.muted }}>
            本次收益
          </div>
          <div className="text-lg font-bold" style={{ color: UI.success }}>
            +¥{submission.thisReward ? formatMoney(submission.thisReward) : "?"}
          </div>
        </div>
      </div>

      {submission.proof && (
        <div className="rounded-2xl overflow-hidden mb-3" style={{ maxHeight: 220, border: `1px solid ${UI.border}` }}>
          <img src={submission.proof} alt="proof" className="w-full object-cover" />
        </div>
      )}

      {submission.note && (
        <div
          className="rounded-xl p-3 mb-3 text-sm"
          style={{ background: "rgba(15,23,42,.03)", border: `1px solid ${UI.border}`, color: UI.muted }}
        >
          {submission.note}
        </div>
      )}

      {!showReject ? (
        <div className="flex gap-2">
          <Button variant="success" className="flex-1" onClick={() => onApprove(submission.id)}>
            通过
          </Button>
          <Button variant="danger" className="flex-1" onClick={() => setShowReject(true)}>
            拒绝
          </Button>
        </div>
      ) : (
        <div>
          <Textarea
            placeholder="拒绝原因（孩子可见）"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="mb-2"
            style={{ borderColor: "rgba(220,38,38,.20)" }}
          />
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowReject(false)}>
              取消
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              disabled={!reason.trim()}
              onClick={() => reason.trim() && onReject(submission.id, reason.trim())}
            >
              确认拒绝
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════
   ASSET CARD (总资产卡片)
═══════════════════════════════════════════════ */
function AssetCard({ wallet, savings, tasks }) {
  const [expanded, setExpanded] = useState(false);
  
  const investment = tasks?.filter(t => t.investment).reduce((sum, t) => sum + t.investment, 0) || 0;
  const totalAssets = wallet + savings.balance + investment;
  const totalInterest = savings.interest || 0;
  
  const assetItems = [
    {
      key: "wallet",
      label: "钱包",
      value: wallet,
      icon: <IconCreditCard className="w-4 h-4" />,
      color: "#6366f1",
      bg: "rgba(99,102,241,.10)",
      desc: "随时可用"
    },
    {
      key: "savings",
      label: "储蓄",
      value: savings.balance,
      icon: <IconBank className="w-4 h-4" />,
      color: "#10b981",
      bg: "rgba(16,185,129,.10)",
      desc: "安全增值"
    },
    {
      key: "investment",
      label: "投资",
      value: investment,
      icon: <IconTrendingUp className="w-4 h-4" />,
      color: "#f59e0b",
      bg: "rgba(245,158,11,.10)",
      desc: "任务投资"
    },
    {
      key: "interest",
      label: "累计利息",
      value: totalInterest,
      icon: <IconMoney className="w-4 h-4" />,
      color: "#ec4899",
      bg: "rgba(236,72,153,.10)",
      desc: "被动收益"
    },
  ];

  return (
    <Card
      className="overflow-hidden transition-all duration-300"
      style={{
        background: "#fff",
        boxShadow: "0 4px 20px rgba(15,23,42,.08)",
        border: `1px solid ${UI.border}`,
      }}
    >
      {/* 头部 - 总资产 */}
      <div
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-medium" style={{ color: UI.muted }}>
              总资产
            </div>
            <div className="text-3xl font-bold mt-1" style={{ color: UI.text }}>
              ¥{formatMoney(totalAssets)}
            </div>
          </div>
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center transition-transform duration-300"
            style={{ background: UI.primarySoft }}
          >
            <span
              className="text-lg"
              style={{ 
                color: UI.primary,
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)" 
              }}
            >
              ▼
            </span>
          </div>
        </div>
        
        {/* 快捷预览 */}
        {!expanded && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            {assetItems.slice(0, 3).map((item) => (
              <div
                key={item.key}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full shrink-0"
                style={{ background: item.bg }}
              >
                <span className="text-xs">{item.icon}</span>
                <span className="text-xs font-medium" style={{ color: item.color }}>
                  ¥{formatMoney(item.value)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 展开详情 */}
      {expanded && (
        <div className="px-4 pb-4">
          <div className="h-px mb-3" style={{ background: UI.border }} />
          
          <div className="grid grid-cols-2 gap-2">
            {assetItems.map((item) => (
              <div
                key={item.key}
                className="p-3 rounded-xl"
                style={{ background: item.bg, border: `1px solid ${item.color}20` }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{item.icon}</span>
                  <span className="text-xs font-medium" style={{ color: item.color }}>
                    {item.label}
                  </span>
                </div>
                <div className="text-lg font-bold" style={{ color: UI.text }}>
                  ¥{formatMoney(item.value)}
                </div>
                <div className="text-xs mt-0.5" style={{ color: UI.muted }}>
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
          
          {/* 收益提示 */}
          {totalInterest > 0 && (
            <div
              className="mt-3 p-2.5 rounded-lg text-center text-xs"
              style={{ background: "rgba(16,185,129,.08)", border: "1px solid rgba(16,185,129,.15)" }}
            >
              <span className="flex items-center gap-1" style={{ color: UI.success }}>
                <IconLightbulb className="w-4 h-4" />
                储蓄利息已累计收益 +¥{formatMoney(totalInterest)}
              </span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════
   WALLET + SAVINGS
═══════════════════════════════════════════════ */
function WalletCard({ wallet, savings, onSave, onWithdraw, rateInfo, rateFetching, logs }) {
  const [dir, setDir] = useState("toSavings"); // toSavings | toWallet
  const [amt, setAmt] = useState("");

  const amount = Number(amt);
  const max = dir === "toSavings" ? wallet : savings.balance;
  const valid = Number.isFinite(amount) && amount > 0 && amount <= max;

  const ctaStyle =
    dir === "toSavings"
      ? { background: UI.accentGreenSoft, border: "1px solid rgba(34,197,94,.28)", color: "#166534" }
      : { background: UI.accentCyanSoft, border: "1px solid rgba(6,182,212,.28)", color: "#0e7490" };

  const action = () => {
    if (!valid) return;
    if (dir === "toSavings") onSave(amount);
    else onWithdraw(amount);
    setAmt("");
  };

  const presets = [10, 20, 50, 100];

  return (
    <div>
      <Card className="p-4 mb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold" style={{ color: UI.text }}>
            转账
          </div>
          <div className="text-xs" style={{ color: UI.muted }}>
            钱包 ↔ 储蓄罐
          </div>
        </div>

        {/* direction */}
        <div
          className="flex rounded-full overflow-hidden mb-3"
          style={{ background: "rgba(15,23,42,.03)", border: `1px solid ${UI.border}` }}
        >
          <button
            className="flex-1 py-2 text-sm font-semibold"
            onClick={() => setDir("toSavings")}
            style={{ background: dir === "toSavings" ? UI.accentGreenSoft : "transparent", color: dir === "toSavings" ? "#166534" : UI.muted }}
          >
            存入储蓄
          </button>
          <button
            className="flex-1 py-2 text-sm font-semibold"
            onClick={() => setDir("toWallet")}
            style={{ background: dir === "toWallet" ? UI.accentCyanSoft : "transparent", color: dir === "toWallet" ? "#0e7490" : UI.muted }}
          >
            取出到钱包
          </button>
        </div>

        {/* amount */}
        <div className="flex items-center gap-2">
          <div
            className="px-3 py-2 rounded-xl text-sm font-semibold"
            style={{ background: "rgba(15,23,42,.03)", border: `1px solid ${UI.border}`, color: UI.muted }}
          >
            ¥
          </div>
          <Input
            type="number"
            inputMode="decimal"
            placeholder={`最多 ¥${formatMoney(max)}`}
            value={amt}
            onChange={(e) => setAmt(e.target.value)}
            style={{ borderColor: !amt ? UI.border : valid ? "rgba(15,23,42,.18)" : "rgba(220,38,38,.22)" }}
          />
          <Button
            variant="subtle"
            className="shrink-0"
            onClick={() => setAmt(String(Math.floor(max * 100) / 100))}
            disabled={max <= 0}
          >
            全部
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mt-2">
          {presets.map((p) => (
            <button
              key={p}
              onClick={() => setAmt(String(p))}
              className="px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: "rgba(15,23,42,.03)", border: `1px solid ${UI.border}`, color: UI.muted }}
            >
              {p}
            </button>
          ))}
        </div>

        <div className="mt-2 text-xs" style={{ color: !amt || valid ? UI.muted : UI.danger }}>
          {!amt ? "输入金额后点击确认" : valid ? `本次将${dir === "toSavings" ? "从钱包转入储蓄" : "从储蓄转出到钱包"}` : "金额不合法或余额不足"}
        </div>

        <Button className="w-full mt-3" disabled={!valid} onClick={action} style={ctaStyle}>
          确认转账
        </Button>
      </Card>

      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-bold" style={{ color: UI.text }}>
            今日基准利率
          </div>
          <div className="text-xs" style={{ color: UI.muted }}>
            {rateFetching ? "获取中…" : rateInfo?.source || "-"}
          </div>
        </div>

        {rateFetching && (
          <div className="text-xs" style={{ color: UI.muted }}>
            正在获取当日利率…
          </div>
        )}

        {!rateFetching && rateInfo && (
          <div className="grid grid-cols-3 gap-2">
            {[
                { label: "活期", val: `${(rateInfo.demand * 100).toFixed(2)}%` },
                { label: "一年", val: `${(rateInfo.term1y * 100).toFixed(2)}%` },
                { label: "三年", val: `${(rateInfo.term3y * 100).toFixed(2)}%` },
              ].map((r) => (
              <div
                key={r.label}
                className="rounded-xl p-2.5"
                style={{ background: "rgba(15,23,42,.03)", border: `1px solid ${UI.border}` }}
              >
                <div className="text-sm font-bold" style={{ color: UI.text }}>
                  {r.val}
                </div>
                <div className="text-xs mt-0.5" style={{ color: UI.muted }}>
                  {r.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {!rateFetching && rateInfo && (
          <div className="text-xs mt-2" style={{ color: UI.muted }}>
            {rateInfo.note} · {rateInfo.date}
          </div>
        )}
      </Card>

      <Card className="p-4 mb-3">
        <div className="text-sm font-bold mb-2" style={{ color: UI.text }}>
          每日结算说明
        </div>
        <div className="text-xs leading-5 space-y-1" style={{ color: UI.muted }}>
          <div>• 利率来自中国银行数据（打开页面自动获取）</div>
          <div>• 储蓄利息按 活期年利率 ÷ 365 计算并每日结算</div>
          <div>• 童信分可叠加额外加成利率，鼓励好习惯</div>
        </div>
      </Card>

      <LogPanel logs={logs} />
    </div>
  );
}

/* ═══════════════════════════════════════════════
   LOG PANEL (with filter and collapse)
═══════════════════════════════════════════════ */
function LogPanel({ logs }) {
  const [expanded, setExpanded] = useState(false);
  const [dateFilter, setDateFilter] = useState("all"); // all, today, week, month
  const [showFilter, setShowFilter] = useState(false);

  // 获取今天的日期字符串
  const getTodayStr = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  // 根据日期筛选并排序（日期近的排在上方）
  const filteredLogs = (() => {
    let filtered = logs;
    
    if (dateFilter !== "all") {
      const now = new Date();
      const todayStr = getTodayStr();
      
      if (dateFilter === "today") {
        filtered = logs.filter(l => l.time?.startsWith(todayStr));
      } else if (dateFilter === "week") {
        // 获取本周一
        const dayOfWeek = now.getDay();
        const monday = new Date(now);
        monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        monday.setHours(0, 0, 0, 0);
        filtered = logs.filter(l => {
          if (!l.time) return false;
          const logDate = new Date(l.time.split(' ')[0]);
          return logDate >= monday;
        });
      } else if (dateFilter === "month") {
        // 获取本月第一天
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        filtered = logs.filter(l => {
          if (!l.time) return false;
          const logDate = new Date(l.time.split(' ')[0]);
          return logDate >= firstDayOfMonth;
        });
      }
    }
    
    // 按时间倒序排列（最近的在前）
    return [...filtered].sort((a, b) => {
      // 如果没有时间，排在最后
      if (!a.time) return 1;
      if (!b.time) return -1;
      // 倒序排列（新的在前）
      return new Date(b.time) - new Date(a.time);
    });
  })();

  const displayLogs = expanded ? filteredLogs : filteredLogs.slice(0, 3);
  const hasMore = filteredLogs.length > 3;

  const filterOptions = [
    { key: "all", label: "全部" },
    { key: "today", label: "今日" },
    { key: "week", label: "本周" },
    { key: "month", label: "本月" },
  ];

  const getFilterLabel = (key) => filterOptions.find(o => o.key === key)?.label || "全部";

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-bold" style={{ color: UI.text }}>
          账本记录
          {dateFilter !== "all" && (
            <span className="ml-2 text-xs font-normal" style={{ color: UI.muted }}>
              ({getFilterLabel(dateFilter)})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {logs.length > 0 && (
            <button
              onClick={() => setShowFilter(!showFilter)}
              className="text-xs px-2 py-1 rounded-full"
              style={{
                background: showFilter ? UI.primarySoft : 'rgba(15,23,42,.06)',
                color: showFilter ? UI.primary : UI.muted,
                border: `1px solid ${showFilter ? UI.primary : UI.border}`
              }}
            >
              筛选
            </button>
          )}
          {filteredLogs.length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs px-2 py-1 rounded-full"
              style={{
                background: 'rgba(15,23,42,.06)',
                color: UI.muted,
                border: `1px solid ${UI.border}`
              }}
            >
              {expanded ? '收起' : '展开'}
            </button>
          )}
        </div>
      </div>

      {/* 日期筛选 */}
      {showFilter && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {filterOptions.map(option => (
            <button
              key={option.key}
              onClick={() => setDateFilter(option.key)}
              className="text-xs px-2 py-1 rounded-full"
              style={{
                background: dateFilter === option.key ? UI.primarySoft : 'rgba(15,23,42,.04)',
                color: dateFilter === option.key ? UI.primary : UI.muted,
                border: `1px solid ${dateFilter === option.key ? UI.primary : UI.border}`
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {filteredLogs.length === 0 && (
        <div className="text-sm text-center py-4" style={{ color: UI.muted }}>
          {dateFilter !== "all" ? `${getFilterLabel(dateFilter)}暂无记录` : '暂无记录'}
        </div>
      )}

      {displayLogs.map((l, i) => (
        <div
          key={i}
          className="flex items-center justify-between py-2"
          style={{ borderBottom: i < displayLogs.length - 1 ? `1px solid ${UI.border}` : 'none' }}
        >
          <div className="min-w-0 flex-1 mr-2">
            <div className="text-sm font-semibold truncate" style={{ color: UI.text }}>
              {l.title}
            </div>
            <div className="text-xs" style={{ color: UI.muted }}>
              {l.time}
            </div>
          </div>
          <div className="text-sm font-bold shrink-0" style={{ color: l.amount > 0 ? UI.success : UI.danger }}>
            {l.amount > 0 ? "+" : ""}¥{formatMoney(l.amount)}
          </div>
        </div>
      ))}

      {!expanded && hasMore && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full mt-3 py-2 text-xs text-center rounded-xl"
          style={{ color: UI.primary, background: UI.primarySoft }}
        >
          查看更多 ({filteredLogs.length - 3} 条)
        </button>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════
   TASK LIST (with filter and optimized layout)
═══════════════════════════════════════════════ */
function TaskList({ tasks, creditScore, onAccept, onSubmitProof, onEarlyWithdraw }) {
  const [filter, setFilter] = useState("all"); // all, pending, active, completed
  const [sortBy, setSortBy] = useState("default"); // default, yield, investment
  const [expandedTypes, setExpandedTypes] = useState({
    short: true,
    long: true,
    cycle_week: true,
    cycle_month: true,
  });

  const taskTypes = [
    { key: "short", label: "单次任务", icon: <IconZap className="w-4 h-4" /> },
    { key: "long", label: "连续打卡", icon: <IconCalendar className="w-4 h-4" /> },
    { key: "cycle_week", label: "周期·周", icon: <IconRefresh className="w-4 h-4" /> },
    { key: "cycle_month", label: "周期·月", icon: <IconCalendar className="w-4 h-4" /> },
  ];

  const filterOptions = [
    { key: "all", label: "全部", count: tasks.length },
    { key: "pending", label: "待接取", count: tasks.filter(t => !t.investment && !t.completed).length },
    { key: "active", label: "进行中", count: tasks.filter(t => t.investment && !t.completed && t.reviewStatus !== "approved").length },
    { key: "completed", label: "已完成", count: tasks.filter(t => t.completed || t.reviewStatus === "approved").length },
  ];

  const getFilteredTasks = (type) => {
    let filtered = tasks.filter((t) => t.type === type);

    // 应用筛选
    switch (filter) {
      case "pending":
        filtered = filtered.filter((t) => !t.investment && !t.completed);
        break;
      case "active":
        filtered = filtered.filter((t) => t.investment && !t.completed && t.reviewStatus !== "approved");
        break;
      case "completed":
        filtered = filtered.filter((t) => t.completed || t.reviewStatus === "approved");
        break;
      default:
        break;
    }

    // 应用排序
    switch (sortBy) {
      case "yield":
        filtered = [...filtered].sort((a, b) => b.yieldRate - a.yieldRate);
        break;
      case "investment":
        filtered = [...filtered].sort((a, b) => (b.investment || 0) - (a.investment || 0));
        break;
      default:
        break;
    }

    return filtered;
  };

  const toggleType = (type) => {
    setExpandedTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  return (
    <div>
      {/* 筛选栏 */}
      <Card className="p-3 mb-3" style={{ boxShadow: "0 2px 8px rgba(15,23,42,.04)" }}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold" style={{ color: UI.muted }}>
            筛选
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-xs px-2 py-1 rounded-lg border outline-none"
            style={{ borderColor: UI.border, color: UI.text, background: UI.panel }}
          >
            <option value="default">默认排序</option>
            <option value="yield">收益率排序</option>
            <option value="investment">投资额排序</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {filterOptions.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setFilter(opt.key)}
              className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
              style={{
                background: filter === opt.key ? UI.primarySoft : "rgba(15,23,42,.04)",
                color: filter === opt.key ? UI.primary : UI.muted,
                border: `1px solid ${filter === opt.key ? UI.primary : UI.border}`,
              }}
            >
              {opt.label} ({opt.count})
            </button>
          ))}
        </div>
      </Card>

      {/* 任务列表 */}
      {taskTypes.map(({ key, label, icon }) => {
        const list = getFilteredTasks(key);
        if (!list.length) return null;

        return (
          <div key={key} className="mb-3">
            {/* 类型标题 */}
            <button
              onClick={() => toggleType(key)}
              className="w-full flex items-center justify-between px-1 py-2 mb-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{icon}</span>
                <span className="text-sm font-bold" style={{ color: UI.text }}>
                  {label}
                </span>
                <span
                  className="text-xs px-1.5 py-0.5 rounded-full"
                  style={{ background: "rgba(15,23,42,.06)", color: UI.muted }}
                >
                  {list.length}
                </span>
              </div>
              <span
                className="text-xs transition-transform"
                style={{
                  color: UI.muted,
                  transform: expandedTypes[key] ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                ▼
              </span>
            </button>

            {/* 任务卡片 */}
            {expandedTypes[key] && (
              <div className="space-y-2">
                {list.map((t) => (
                  <OptimizedTaskCard
                    key={t.id}
                    task={t}
                    creditScore={creditScore}
                    onAccept={onAccept}
                    onSubmitProof={onSubmitProof}
                    onEarlyWithdraw={onEarlyWithdraw}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* 空状态 */}
      {taskTypes.every(({ key }) => getFilteredTasks(key).length === 0) && (
        <Card className="p-8 text-center" style={{ boxShadow: "none" }}>
          <div className="flex justify-center mb-3">
            <IconEdit className="w-16 h-16" style={{ color: UI.primary }} />
          </div>
          <div className="text-sm font-semibold mb-1" style={{ color: UI.text }}>
            暂无任务
          </div>
          <div className="text-xs" style={{ color: UI.muted }}>
            当前筛选条件下没有任务
          </div>
        </Card>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   CREDIT PANEL (优化版)
═══════════════════════════════════════════════ */
function CreditPanel({ score, rateInfo }) {
  const [showDetails, setShowDetails] = useState(false);
  const [animatedScore, setAnimatedScore] = useState(500);
  
  const tiers = [
    { min: 500, max: 599, bonus: "无加成", label: "新手", c: "#94a3b8", icon: <IconSprout className="w-4 h-4" />, desc: "起步阶段" },
    { min: 600, max: 699, bonus: "+0.5%", label: "成长", c: "#6366f1", icon: <IconLeaf className="w-4 h-4" />, desc: "稳步提升" },
    { min: 700, max: 799, bonus: "+1.0%", label: "优秀", c: "#10b981", icon: <IconTree className="w-4 h-4" />, desc: "表现良好" },
    { min: 800, max: 1000, bonus: "+2.0%", label: "卓越", c: "#f59e0b", icon: <IconCrown className="w-4 h-4" />, desc: "信用极佳" },
  ];
  
  const cur = tiers.find((t) => score >= t.min && score <= t.max) || tiers[0];
  const nextTier = tiers.find((t) => t.min > score);
  const bonusNum = score >= 800 ? 0.02 : score >= 700 ? 0.01 : score >= 600 ? 0.005 : 0;
  const baseRate = rateInfo?.demand ?? 0.0005;
  const effectiveRate = baseRate + bonusNum;
  
  // 计算升级进度
  const progress = nextTier 
    ? ((score - cur.min) / (nextTier.min - cur.min)) * 100 
    : 100;
  
  // 动画效果
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedScore(score);
    }, 100);
    return () => clearTimeout(timer);
  }, [score]);

  return (
    <div className="mb-4">
      {/* 主信用卡片 */}
      <Card 
        className="overflow-hidden transition-all duration-300"
        style={{ 
          boxShadow: `0 8px 32px ${cur.c}25`,
          border: `1px solid ${cur.c}30`
        }}
      >
        {/* 头部渐变背景 */}
        <div 
          className="p-5 relative"
          style={{ 
            background: `linear-gradient(135deg, ${cur.c}15 0%, ${cur.c}08 100%)`
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">{cur.icon}</span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" 
                  style={{ background: cur.c + "20", color: cur.c }}>
                  {cur.label}
                </span>
              </div>
              <div className="text-4xl font-bold mt-2" style={{ color: cur.c }}>
                {animatedScore}
              </div>
              <div className="text-xs mt-1" style={{ color: UI.muted }}>
                童信分
              </div>
            </div>
            
            <div className="text-right">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mb-2"
                style={{ background: cur.c + "15", border: `2px solid ${cur.c}40` }}
              >
                <span className="text-2xl">{cur.icon}</span>
              </div>
            </div>
          </div>
          
          {/* 升级进度条 */}
          {nextTier && (
            <div className="mt-4">
              <div className="flex justify-between text-xs mb-1">
                <span style={{ color: UI.muted }}>升级进度</span>
                <span style={{ color: cur.c }}>
                  {score} / {nextTier.min} → {nextTier.label}
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(15,23,42,.06)" }}>
                <div 
                  className="h-full rounded-full transition-all duration-1000"
                  style={{ 
                    width: `${progress}%`,
                    background: `linear-gradient(90deg, ${cur.c}60 0%, ${cur.c} 100%)`
                  }}
                />
              </div>
            </div>
          )}
          
          {/* 展开详情按钮 */}
          <button 
            onClick={() => setShowDetails(!showDetails)}
            className="w-full mt-4 flex items-center justify-center gap-1 text-xs py-2 rounded-lg transition-colors"
            style={{ background: "rgba(255,255,255,.5)", color: UI.muted }}
          >
            <span>{showDetails ? "收起详情" : "查看详情"}</span>
            <span style={{ transform: showDetails ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s" }}>
              ▼
            </span>
          </button>
        </div>
        
        {/* 展开详情 */}
        {showDetails && (
          <div className="p-4 border-t" style={{ borderColor: UI.border }}>
            {/* 利率信息 */}
            <div className="mb-4">
              <div className="text-xs font-semibold mb-2" style={{ color: UI.muted }}>
                利率加成
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 p-3 rounded-xl" style={{ background: "rgba(15,23,42,.03)" }}>
                  <div className="text-xs" style={{ color: UI.muted }}>基准利率</div>
                  <div className="text-lg font-bold" style={{ color: UI.text }}>
                    {(baseRate * 100).toFixed(4)}%
                  </div>
                </div>
                <div className="text-lg" style={{ color: UI.muted }}>+</div>
                <div className="flex-1 p-3 rounded-xl" style={{ background: cur.c + "10" }}>
                  <div className="text-xs" style={{ color: cur.c }}>信用加成</div>
                  <div className="text-lg font-bold" style={{ color: cur.c }}>
                    {(bonusNum * 100).toFixed(1)}%
                  </div>
                </div>
                <div className="text-lg" style={{ color: UI.muted }}>=</div>
                <div className="flex-1 p-3 rounded-xl" style={{ background: UI.success + "10" }}>
                  <div className="text-xs" style={{ color: UI.success }}>实际利率</div>
                  <div className="text-lg font-bold" style={{ color: UI.success }}>
                    {(effectiveRate * 100).toFixed(4)}%
                  </div>
                </div>
              </div>
            </div>
            
            {/* 等级列表 */}
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color: UI.muted }}>
                等级体系
              </div>
              <div className="space-y-2">
                {tiers.map((t) => {
                  const active = score >= t.min;
                  const isCurrent = score >= t.min && score <= t.max;
                  return (
                    <div
                      key={t.min}
                      className="flex items-center gap-3 p-3 rounded-xl transition-all"
                      style={{
                        background: isCurrent ? t.c + "10" : "rgba(15,23,42,.02)",
                        border: `1px solid ${isCurrent ? t.c + "30" : UI.border}`,
                      }}
                    >
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                        style={{ background: active ? t.c + "20" : "rgba(15,23,42,.06)" }}
                      >
                        {t.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold" style={{ color: active ? t.c : UI.muted }}>
                            {t.label}
                          </span>
                          {isCurrent && (
                            <span 
                              className="text-xs px-1.5 py-0.5 rounded-full"
                              style={{ background: t.c + "20", color: t.c }}
                            >
                              当前
                            </span>
                          )}
                        </div>
                        <div className="text-xs" style={{ color: UI.muted }}>
                          {t.min}-{t.max}分 · {t.desc}
                        </div>
                      </div>
                      <div 
                        className="text-sm font-bold px-3 py-1 rounded-full"
                        style={{ 
                          background: active ? t.c + "15" : "transparent",
                          color: active ? t.c : UI.muted 
                        }}
                      >
                        {t.bonus}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Card>
      
      {/* 信用小贴士 */}
      <Card className="p-3 mt-3" style={{ boxShadow: "0 2px 8px rgba(15,23,42,.04)" }}>
        <div className="flex items-start gap-2">
          <IconLightbulb className="w-5 h-5 flex-shrink-0" style={{ color: UI.warn }} />
          <div>
            <div className="text-xs font-semibold" style={{ color: UI.text }}>
              信用小贴士
            </div>
            <div className="text-xs mt-0.5" style={{ color: UI.muted }}>
              {score < 600 
                ? "按时完成任务可以提升信用分，获得更高利率加成！"
                : score < 700 
                ? "表现不错！继续保持，向优秀等级迈进！"
                : score < 800
                ? "信用优秀！你的努力得到了回报！"
                : "信用卓越！你已经是榜样了！"
              }
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   HISTORY LOG
═══════════════════════════════════════════════ */
function HistoryLog({ logs }) {
  return (
    <Card className="p-4">
      <div className="text-sm font-bold mb-3" style={{ color: UI.text }}>
        账本记录
      </div>
      {logs.length === 0 && (
        <div className="text-sm text-center py-4" style={{ color: UI.muted }}>
          暂无记录
        </div>
      )}
      {[...logs].reverse().map((l, i) => (
        <div key={i} className="flex items-center justify-between py-2" style={{ borderBottom: `1px solid ${UI.border}` }}>
          <div>
            <div className="text-sm font-semibold" style={{ color: UI.text }}>
              {l.title}
            </div>
            <div className="text-xs" style={{ color: UI.muted }}>
              {l.time}
            </div>
          </div>
          <div className="text-sm font-bold" style={{ color: l.amount > 0 ? UI.success : UI.danger }}>
            {l.amount > 0 ? "+" : ""}¥{formatMoney(l.amount)}
          </div>
        </div>
      ))}
    </Card>
  );
}

/* ═══════════════════════════════════════════════
   INITIAL DATA
═══════════════════════════════════════════════ */
const INIT_TASKS = [
  {
    id: 1,
    type: "short",
    title: "完成一篇阅读笔记",
    desc: "读书后写下50字以上感悟",
    yieldRate: 0.15,
    completed: false,
    reviewStatus: null,
    proof: null,
    investment: null,
  },
  {
    id: 2,
    type: "long",
    title: "连续7天早睡打卡",
    desc: "每天晚上9点前上床",
    yieldRate: 0.2,
    totalDays: 7,
    daysCompleted: 0,
    completed: false,
    reviewStatus: null,
    proof: null,
    investment: null,
  },
  {
    id: 3,
    type: "cycle_week",
    title: "每周锻炼组合",
    desc: "完成本周选定的锻炼打卡日即可结算",
    yieldRate: 0.12,
    requiredDays: [0, 2, 4],
    cycleCheckins: [],
    cyclesCompleted: 0,
    completed: false,
    reviewStatus: null,
    proof: null,
    investment: null,
  },
  {
    id: 4,
    type: "cycle_month",
    title: "每月阅读计划",
    desc: "完成本月选定的阅读打卡日",
    yieldRate: 0.18,
    requiredDays: [1, 8, 15, 22, 29],
    cycleCheckins: [],
    cyclesCompleted: 0,
    completed: false,
    reviewStatus: null,
    proof: null,
    investment: null,
  },
];

/* ═══════════════════════════════════════════════
   ROOT APP
═══════════════════════════════════════════════ */
const AVATARS = [
  { emoji: "👦", color: "#60a5fa", type: "emoji" },
  { emoji: "👧", color: "#f472b6", type: "emoji" },
  { emoji: "🧑", color: "#34d399", type: "emoji" },
  { emoji: "🧒", color: "#fbbf24", type: "emoji" },
];

export default function App() {
  // PWA 自动更新（后台静默更新）
  const { offlineReady, updateAvailable, applyUpdate } = usePWAUpdate();

  const [mode, setMode] = useState("child");
  const [tab, setTab] = useState("home");
  const [currentMemberId, setCurrentMemberId] = useState(1);
  const [members, setMembers] = useState([
    { id: 1, name: "宝贝 1", avatar: 0, avatarType: "emoji", avatarData: null, wallet: 120, savings: { balance: 80, interest: 3.2 }, creditScore: 650, tasks: INIT_TASKS, logs: [], lastSettledDate: null },
  ]);
  const [submissions, setSubmissions] = useState([]);
  const [toast, setToast] = useState(null);
  const [proofModal, setProofModal] = useState(null);
  const [acceptModal, setAcceptModal] = useState(null);
  const [checkinHistoryModal, setCheckinHistoryModal] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [memberModal, setMemberModal] = useState(false);
  const [newMemberForm, setNewMemberForm] = useState({
    name: "",
    avatar: 0,
    avatarType: "emoji",
    avatarData: null,
    wallet: 120,
    savingsBalance: 80,
    creditScore: 650,
  });
  const [editMemberId, setEditMemberId] = useState(null);
  const [editMemberForm, setEditMemberForm] = useState({
    name: "",
    avatar: 0,
    avatarType: "emoji",
    avatarData: null,
  });
  const [editTaskId, setEditTaskId] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // PWA 安装提示
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  const [rateInfo, setRateInfo] = useState(null);
  const [rateFetching, setRateFetching] = useState(false);
  const contentRef = useRef(null);

  // PWA 安装提示逻辑
  useEffect(() => {
    // 监听 beforeinstallprompt 事件
    const handleBeforeInstallPrompt = (e) => {
      // 阻止默认提示
      e.preventDefault();
      // 保存事件以便后续使用
      setInstallPrompt(e);
      // 检查是否已经安装过或用户是否已关闭提示
      const hasInstalled = localStorage.getItem('pwa-installed');
      const installDismissed = localStorage.getItem('pwa-install-dismissed');
      const dismissedTime = installDismissed ? parseInt(installDismissed) : 0;
      const oneDay = 24 * 60 * 60 * 1000;
      
      if (!hasInstalled && (!dismissedTime || Date.now() - dismissedTime > oneDay)) {
        // 延迟显示提示，避免页面加载时立即弹出
        setTimeout(() => {
          setShowInstallBanner(true);
        }, 2000);
      }
    };

    // 监听 appinstalled 事件
    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setShowInstallBanner(false);
      localStorage.setItem('pwa-installed', 'true');
      toast$('应用已安装到主屏幕', 'success');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // 处理安装按钮点击
  const handleInstallClick = async () => {
    if (!installPrompt) return;
    
    // 显示安装提示
    installPrompt.prompt();
    
    // 等待用户响应
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstallBanner(false);
      localStorage.setItem('pwa-installed', 'true');
    } else {
      // 用户拒绝，记录时间以便 24 小时后再次提示
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
      setShowInstallBanner(false);
    }
    
    // 清除保存的提示事件
    setInstallPrompt(null);
  };

  // 关闭安装提示
  const handleDismissInstall = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const currentMember = members.find(m => m.id === currentMemberId) || members[0];
  const { wallet, savings, creditScore, tasks, logs, lastSettledDate } = currentMember;

  const saveToDB = useCallback(async (data) => {
    try {
      await saveData(data);
    } catch (error) {
      console.error('保存数据失败:', error);
    }
  }, []);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    
    loadData()
      .then((savedData) => {
        if (savedData && savedData.members && Array.isArray(savedData.members)) {
          setMembers(savedData.members);
          setCurrentMemberId(savedData.currentMemberId ?? 1);
          if (savedData.submissions && Array.isArray(savedData.submissions)) {
            setSubmissions(savedData.submissions);
          }
        }
        setDataLoaded(true);
      })
      .catch((error) => {
        console.error('加载数据失败:', error);
        setDataLoaded(true);
      });

    setRateFetching(true);
    fetchTodayRates()
      .then((rates) => {
        setRateInfo(rates);
        setRateFetching(false);

        if (lastSettledDate !== today && savings.balance > 0) {
          const bonusRate =
            creditScore >= 800 ? 0.02 : creditScore >= 700 ? 0.01 : creditScore >= 600 ? 0.005 : 0;
          const effectiveRate = rates.demand + bonusRate;
          const dailyInterest = Math.round(((savings.balance * effectiveRate) / 365) * 10000) / 10000;

          if (dailyInterest > 0) {
            updateCurrentMember({
              savings: {
                balance: Math.round((savings.balance + dailyInterest) * 10000) / 10000,
                interest: Math.round((savings.interest + dailyInterest) * 10000) / 10000,
              },
              logs: [...(logs || []), {
                title: `今日利息结算 (${(effectiveRate * 100).toFixed(2)}%/年)`,
                amount: dailyInterest,
                time: today,
              }],
              lastSettledDate: today,
            });
          }
        }
      })
      .catch(() => {
        setRateFetching(false);
        setRateInfo({
          demand: 0.0005,
          term1y: 0.0095,
          term3y: 0.0125,
          source: "中国银行 (离线)",
          date: today,
          note: "自 2025 年 5 月 20 日起执行",
        });
      });
  }, []);

  useEffect(() => {
    if (dataLoaded) {
      saveToDB({
        members,
        currentMemberId,
        submissions,
      });
    }
  }, [members, currentMemberId, submissions, dataLoaded, saveToDB]);

  const toast$ = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2400);
  };
  
  const updateCurrentMember = (updates) => {
    setMembers(prev => prev.map(m =>
      m.id === currentMemberId ? { ...m, ...updates } : m
    ));
  };

  // 更新指定成员的数据（用于审核时更新提交者）
  const updateMemberById = (memberId, updates) => {
    setMembers(prev => prev.map(m =>
      m.id === memberId ? { ...m, ...updates } : m
    ));
  };
  
  const log$ = (entry) => {
    updateCurrentMember({
      logs: [...(currentMember.logs || []), { ...entry, time: "刚刚" }]
    });
  };

  const handleExport = () => {
    exportToJSON({
      members,
      currentMemberId,
    });
    toast$("导出成功");
  };

  const handleImport = async (file) => {
    try {
      const imported = await importFromJSON(file);
      if (imported.members && Array.isArray(imported.members)) {
        setMembers(imported.members);
        setCurrentMemberId(imported.currentMemberId || 1);
      } else {
        const importedData = imported;
        updateCurrentMember({
          wallet: importedData.wallet,
          savings: importedData.savings,
          creditScore: importedData.creditScore,
          tasks: importedData.tasks,
          logs: importedData.logs || [],
          lastSettledDate: importedData.lastSettledDate || null,
        });
      }
      setImportModal(false);
      toast$("导入成功");
    } catch (error) {
      toast$(error.message, "error");
    }
  };

  const handleClearData = async () => {
    if (window.confirm('确定要清空所有数据吗？此操作不可恢复！')) {
      await clearData();
      setMembers([
        { id: 1, name: "宝贝 1", avatar: 0, avatarType: "emoji", avatarData: null, wallet: 120, savings: { balance: 80, interest: 3.2 }, creditScore: 650, tasks: INIT_TASKS, logs: [], lastSettledDate: null },
      ]);
      setCurrentMemberId(1);
      toast$("数据已清空");
    }
  };

  const handleSwitchMember = (memberId) => {
    setCurrentMemberId(memberId);
  };

  const handleOpenAddMember = () => {
    const newId = Math.max(...members.map(m => m.id)) + 1;
    setNewMemberForm({
      name: `宝贝 ${newId}`,
      avatar: (newId - 1) % AVATARS.length,
      avatarType: "emoji",
      avatarData: null,
      wallet: 120,
      savingsBalance: 80,
      creditScore: 650,
    });
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast$("请选择图片文件", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setNewMemberForm({
        ...newMemberForm,
        avatarType: "image",
        avatarData: base64,
        avatar: -1,
      });
      toast$("头像已上传");
    };
    reader.onerror = () => {
      toast$("图片上传失败", "error");
    };
    reader.readAsDataURL(file);
  };

  const handleAddMember = () => {
    const newId = Math.max(...members.map(m => m.id)) + 1;
    if (!newMemberForm.name.trim()) {
      toast$("请输入成员名称", "error");
      return;
    }
    setMembers(prev => [...prev, {
      id: newId,
      name: newMemberForm.name.trim(),
      avatar: newMemberForm.avatarType === "emoji" ? newMemberForm.avatar : -1,
      avatarType: newMemberForm.avatarType,
      avatarData: newMemberForm.avatarData,
      wallet: Number(newMemberForm.wallet) || 0,
      savings: { balance: Number(newMemberForm.savingsBalance) || 0, interest: 0 },
      creditScore: Number(newMemberForm.creditScore) || 650,
      tasks: INIT_TASKS.map(t => ({ ...t, id: t.id + Date.now() })),
      logs: [],
      lastSettledDate: null,
    }]);
    setNewMemberForm({
      name: "",
      avatar: 0,
      avatarType: "emoji",
      avatarData: null,
      wallet: 120,
      savingsBalance: 80,
      creditScore: 650,
    });
    toast$("成员已添加");
  };

  const handleRemoveMember = (memberId) => {
    if (members.length <= 1) {
      toast$("至少保留一个成员", "error");
      return;
    }
    if (window.confirm('确定要删除该成员吗？')) {
      setMembers(prev => prev.filter(m => m.id !== memberId));
      if (currentMemberId === memberId) {
        setCurrentMemberId(members.find(m => m.id !== memberId)?.id || 1);
      }
    }
  };

  const handleOpenEditMember = (member) => {
    setEditMemberId(member.id);
    setEditMemberForm({
      name: member.name,
      avatar: member.avatar,
      avatarType: member.avatarType || "emoji",
      avatarData: member.avatarData || null,
    });
  };

  const handleAvatarUploadEdit = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast$("请选择图片文件", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setEditMemberForm({
        ...editMemberForm,
        avatarType: "image",
        avatarData: base64,
        avatar: -1,
      });
      toast$("头像已上传");
    };
    reader.onerror = () => {
      toast$("图片上传失败", "error");
    };
    reader.readAsDataURL(file);
  };

  const handleSaveEditMember = () => {
    if (!editMemberForm.name.trim()) {
      toast$("请输入成员名称", "error");
      return;
    }
    setMembers(prev => prev.map(m => 
      m.id === editMemberId ? {
        ...m,
        name: editMemberForm.name.trim(),
        avatar: editMemberForm.avatarType === "emoji" ? editMemberForm.avatar : -1,
        avatarType: editMemberForm.avatarType,
        avatarData: editMemberForm.avatarData,
      } : m
    ));
    setEditMemberId(null);
    setEditMemberForm({
      name: "",
      avatar: 0,
      avatarType: "emoji",
      avatarData: null,
    });
    toast$("成员信息已更新");
  };

  const handleCancelEdit = () => {
    setEditMemberId(null);
    setEditMemberForm({
      name: "",
      avatar: 0,
      avatarType: "emoji",
      avatarData: null,
    });
  };

  const handleAccept = (taskId, amount, cycles = 1) => {
    updateCurrentMember({
      wallet: wallet - amount,
      tasks: tasks.map((t) => (t.id === taskId ? { ...t, investment: amount, cycles: cycles } : t)),
    });
    log$({ title: `投资任务: ${tasks.find((t) => t.id === taskId)?.title}`, amount: -amount });
    setAcceptModal(null);
    toast$(`已投入 ¥${amount}`);
  };

  const handleSubmitProof = (taskId, proof, note) => {
    const task = tasks.find((t) => t.id === taskId);
    const isCycle = task.type === "cycle_week" || task.type === "cycle_month";

    // 初始化 currentCycleCheckins 为 null，在 isCycle 为 true 时会被重新赋值
    let currentCycleCheckins = null;

    if (isCycle) {
      const today = new Date();
      const todayStr = today.toISOString().slice(0, 10);
      const currentDayOfWeek = today.getDay();
      const currentDayOfMonth = today.getDate();

      // 只检查当前周期的打卡记录
      const currentCycleIndex = (task.completedCycles || 0) + 1;
      currentCycleCheckins = (task.cycleCheckins || []).filter(c => c.cycleIndex === currentCycleIndex);

      if (task.type === "cycle_week") {
        const alreadyCheckedIn = currentCycleCheckins.some(c => c.date === todayStr);

        if (alreadyCheckedIn) {
          toast$("今天已经打卡过了", "error");
          return;
        }

        const isRequiredDay = task.requiredDays?.includes(currentDayOfWeek);
        if (!isRequiredDay) {
          toast$("今天不是约定的打卡日", "error");
          return;
        }
      } else if (task.type === "cycle_month") {
        const alreadyCheckedIn = currentCycleCheckins.some(c => c.date === todayStr);

        if (alreadyCheckedIn) {
          toast$("今天已经打卡过了", "error");
          return;
        }

        const isRequiredDay = task.requiredDays?.includes(currentDayOfMonth);
        if (!isRequiredDay) {
          toast$("今天不是约定的打卡日", "error");
          return;
        }
      }
    }
    
    // 周期任务使用复利计算，其他任务使用单利计算
    const thisReward = isCycle
      ? Math.round((task.investment + (task.accumulatedEarnings || 0)) * task.yieldRate * 100) / 100
      : calcReturn(task.investment, task.yieldRate);
    
    const sub = {
      id: Date.now(),
      taskId,
      taskTitle: task.title,
      taskType: task.type,
      proof,
      note,
      thisReward,
      isCycleCheckin: isCycle,
      checkinIndex: isCycle ? `${(currentCycleCheckins?.length || 0) + 1}/${task.requiredDays?.length}` : null,
      submittedAt: new Date().toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" }),
      checkinDate: isCycle ? new Date().toISOString().slice(0, 10) : null,
      status: "pending",
      memberId: currentMemberId,
      memberName: currentMember.name,
    };

    setSubmissions((s) => [...s, sub]);
    updateCurrentMember({
      tasks: tasks.map((t) => (t.id === taskId ? { ...t, reviewStatus: "pending", proof } : t)),
    });
    setProofModal(null);
    toast$("凭证已提交，等待审核", "warn");
  };

  const handleEarlyWithdraw = (id) => {
    const task = tasks.find((t) => t.id === id);
    if (!task || !task.investment) return;

    const returned = task.investment;
    updateCurrentMember({
      wallet: wallet + returned,
      creditScore: Math.max(500, creditScore - 10),
    });
    log$({ title: `提前退出: ${task.title}`, amount: returned });
    toast$(`退出任务，本金退回，信用-10`, "warn");

    updateCurrentMember({
      tasks: tasks.map((t) =>
        t.id === id
          ? { ...t, investment: null, reviewStatus: null, proof: null, daysCompleted: 0, cycleCheckins: [], completedCycles: 0, accumulatedEarnings: 0 }
          : t
      ),
    });
  };

  const handleSave = (amt) => {
    if (amt <= 0 || amt > wallet) {
      toast$("余额不足", "error");
      return;
    }
    updateCurrentMember({
      wallet: wallet - amt,
      savings: { ...savings, balance: savings.balance + amt },
    });
    log$({ title: "存入储蓄罐", amount: -amt });
    toast$(`已存入 ¥${amt}`);
  };

  const handleWithdraw = (amt) => {
    if (amt <= 0 || amt > savings.balance) {
      toast$("储蓄余额不足", "error");
      return;
    }
    updateCurrentMember({
      wallet: wallet + amt,
      savings: { ...savings, balance: savings.balance - amt },
    });
    log$({ title: "从储蓄罐取出", amount: amt });
    toast$(`已取出 ¥${amt}`);
  };

  const handleCreateTask = (task) => {
    // 任务在所有成员间共用，将新任务添加到所有成员
    setMembers(prevMembers =>
      prevMembers.map(member => ({
        ...member,
        tasks: [...member.tasks, task],
      }))
    );
    setCreateModal(false);
    toast$("任务已发布（所有成员可见）");
  };

  const handleOpenEditTask = (task) => {
    setEditTaskId(task.id);
    setEditTaskForm({ ...task });
  };

  const handleSaveEditTask = (updatedTask) => {
    // 任务共用，更新所有成员的该任务
    setMembers(prevMembers =>
      prevMembers.map(member => ({
        ...member,
        tasks: member.tasks.map((t) => (t.id === editTaskId ? { ...updatedTask, id: editTaskId } : t)),
      }))
    );
    setEditTaskId(null);
    setEditTaskForm(null);
    toast$("任务已更新（所有成员）");
  };

  const handleCancelEditTask = () => {
    setEditTaskId(null);
    setEditTaskForm(null);
  };

  const handleDeleteTask = (taskId) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    if (task.investment) {
      toast$("该任务已有投资，无法删除", "error");
      return;
    }

    if (window.confirm(`确定要删除任务"${task.title}"吗？此操作不可恢复！`)) {
      // 任务共用，从所有成员中删除该任务
      setMembers(prevMembers =>
        prevMembers.map(member => ({
          ...member,
          tasks: member.tasks.filter((t) => t.id !== taskId),
        }))
      );
      toast$("任务已删除（所有成员）");
    }
  };

  const handleApprove = (subId) => {
    const sub = submissions.find((s) => s.id === subId);
    if (!sub) return;

    // 获取提交审核的成员及其任务
    const member = members.find((m) => m.id === sub.memberId);
    if (!member) return;

    const task = member.tasks.find((t) => t.id === sub.taskId);
    if (!task) return;

    const earned = sub.thisReward;
    const isCycle = task.type === "cycle_week" || task.type === "cycle_month";

    if (isCycle) {
      const checkinRecord = {
        id: sub.id,
        date: sub.checkinDate || new Date().toISOString().slice(0, 10),
        cycleIndex: (task.completedCycles || 0) + 1, // 记录属于第几周期
        proof: sub.proof, // 打卡图片
        note: sub.note, // 打卡备注
        time: sub.submittedAt, // 打卡时间
      };
      // 保留所有历史打卡记录
      const allCheckins = [...(task.cycleCheckins || []), checkinRecord];
      // 计算当前周期的打卡数
      const currentCycleIndex = (task.completedCycles || 0) + 1;
      const currentCycleCheckins = allCheckins.filter(c => c.cycleIndex === currentCycleIndex);
      const cycleComplete = currentCycleCheckins.length >= (task.requiredDays?.length || 1);

      if (cycleComplete) {
        const newCompletedCycles = (task.completedCycles || 0) + 1;
        const allCyclesComplete = newCompletedCycles >= (task.cycles || 1);

        // 复利计算：每个周期的收益基于当前本金（本金 + 累计收益）
        const currentPrincipal = task.investment + (task.accumulatedEarnings || 0);
        const cycleEarning = Math.round(currentPrincipal * task.yieldRate * 100) / 100;
        const newAccumulatedEarnings = (task.accumulatedEarnings || 0) + cycleEarning;

        if (allCyclesComplete) {
          // 最后一个周期：分开记录本金返还和收益入账
          const newLogs = [
            { title: `任务完成收益：${task.title}`, amount: cycleEarning, time: "刚刚" },
            { title: `任务本金返还：${task.title}`, amount: task.investment, time: "刚刚" },
            { title: `累计复利收益：${task.title}`, amount: newAccumulatedEarnings - cycleEarning, time: "刚刚" }
          ];

          updateMemberById(sub.memberId, {
            wallet: member.wallet + task.investment + newAccumulatedEarnings,
            creditScore: Math.min(1000, member.creditScore + 8),
            logs: [...(member.logs || []), ...newLogs],
            tasks: member.tasks.map((t) =>
              t.id === sub.taskId
                ? {
                    ...t,
                    cycleCheckins: allCheckins, // 保留所有打卡记录
                    completedCycles: newCompletedCycles,
                    reviewStatus: "approved",
                    completed: true,
                    investment: null,
                    proof: null,
                  }
                : t
            ),
          });
          toast$(`【${member.name}】周期完成，本期收益 +¥${formatMoney(cycleEarning)}，累计复利收益 ¥${formatMoney(newAccumulatedEarnings)}，本金 ¥${formatMoney(task.investment)} 已返还，所有周期已完成！`);
        } else {
          // 非最后一个周期：只记录收益，收益滚入本金
          const newLogs = [
            ...(member.logs || []),
            { title: `周期复利收益：${task.title}`, amount: cycleEarning, time: "刚刚" }
          ];

          updateMemberById(sub.memberId, {
            wallet: member.wallet + cycleEarning,
            creditScore: Math.min(1000, member.creditScore + 8),
            logs: newLogs,
            tasks: member.tasks.map((t) =>
              t.id === sub.taskId
                ? {
                    ...t,
                    cycleCheckins: allCheckins, // 保留所有打卡记录
                    completedCycles: newCompletedCycles,
                    reviewStatus: null,
                    completed: false,
                    investment: task.investment,
                    accumulatedEarnings: newAccumulatedEarnings,
                    proof: null,
                  }
                : t
            ),
          });
          toast$(`【${member.name}】周期完成，本期收益 +¥${formatMoney(cycleEarning)}（复利滚存）`);
        }
      } else {
        updateMemberById(sub.memberId, {
          tasks: member.tasks.map((t) =>
            t.id === sub.taskId ? { ...t, cycleCheckins: allCheckins, reviewStatus: null, proof: null } : t
          ),
        });
        toast$(`【${member.name}】本次打卡审核通过`);
      }
    } else if (task.type === "long") {
      const newDays = (task.daysCompleted || 0) + 1;
      const done = newDays >= task.totalDays;
      if (done) {
        const total = task.investment + calcReturn(task.investment, task.yieldRate);
        const earned = calcReturn(task.investment, task.yieldRate);
        const newLogs = [
          { title: `任务完成收益：${task.title}`, amount: earned, time: "刚刚" },
          { title: `任务本金返还：${task.title}`, amount: task.investment, time: "刚刚" }
        ];

        updateMemberById(sub.memberId, {
          wallet: member.wallet + total,
          creditScore: Math.min(1000, member.creditScore + 15),
          logs: [...(member.logs || []), ...newLogs],
          tasks: member.tasks.map((t) => (t.id === sub.taskId ? { ...t, daysCompleted: newDays, completed: true, reviewStatus: "approved", investment: null } : t)),
        });
        toast$(`【${member.name}】全部完成，本金 + 收益 ¥${formatMoney(total)} 已到账`);
      } else {
        updateMemberById(sub.memberId, {
          tasks: member.tasks.map((t) => (t.id === sub.taskId ? { ...t, daysCompleted: newDays, reviewStatus: null, proof: null } : t)),
        });
        toast$(`【${member.name}】第 ${newDays}/${task.totalDays} 天通过`);
      }
    } else {
      const total = task.investment + earned;
      const newLogs = [
        { title: `任务完成收益：${task.title}`, amount: earned, time: "刚刚" },
        { title: `任务本金返还：${task.title}`, amount: task.investment, time: "刚刚" }
      ];

      updateMemberById(sub.memberId, {
        wallet: member.wallet + total,
        creditScore: Math.min(1000, member.creditScore + 5),
        logs: [...(member.logs || []), ...newLogs],
        tasks: member.tasks.map((t) => (t.id === sub.taskId ? { ...t, completed: true, reviewStatus: "approved", investment: null } : t)),
      });
      toast$(`【${member.name}】审核通过，本金 + 收益 ¥${formatMoney(total)} 已到账`);
    }

    setSubmissions((prev) =>
      prev.map((s) => (s.id === subId ? { ...s, status: "approved", approvedAt: new Date().toLocaleString("zh-CN") } : s))
    );
  };

  const handleReject = (subId, reason) => {
    const sub = submissions.find((s) => s.id === subId);
    if (!sub) return;

    // 获取提交审核的成员
    const member = members.find((m) => m.id === sub.memberId);
    if (!member) return;

    updateMemberById(sub.memberId, {
      creditScore: Math.max(500, member.creditScore - 5),
      tasks: member.tasks.map((t) =>
        t.id === sub.taskId ? { ...t, reviewStatus: "rejected", rejectReason: reason, proof: null } : t
      ),
    });
    setSubmissions((prev) =>
      prev.map((s) => (s.id === subId ? { ...s, status: "rejected", rejectReason: reason, rejectedAt: new Date().toLocaleString("zh-CN") } : s))
    );
    toast$(`【${member.name}】已拒绝，可重新提交`, "warn");
  };

  const pendingCount = submissions.filter((s) => s.status === "pending").length;

  const childTabs = [
    { id: "home", label: "首页", icon: "home" },
    { id: "tasks", label: "任务", icon: "tasks" },
    { id: "checkins", label: "打卡", icon: "checkin" },
    { id: "bank", label: "银行账本", icon: "bank" },
    { id: "credit", label: "信用", icon: "credit" },
  ];

  const pageTitle = mode === "child" ? "小财神成长营" : "家长管理";

  return (
    <>
      {/* 离线就绪提示 - 3秒后自动消失 */}
      {offlineReady && (
        <div
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[100] px-4 py-2 rounded-full transition-opacity duration-500"
          style={{
            background: UI.success,
            boxShadow: '0 4px 12px rgba(22,163,74,.3)'
          }}
        >
          <div className="flex items-center gap-2 text-white text-sm font-medium">
            <IconCheck className="w-4 h-4" />
            <span>已支持离线使用</span>
          </div>
        </div>
      )}

      {/* 状态栏安全区域遮罩 - 覆盖手机顶部状态栏 */}
      <div 
        className="fixed top-0 left-0 right-0 z-50"
        style={{ 
          height: 'env(safe-area-inset-top, 50px)',
          minHeight: '50px',
          background: UI.bg,
        }}
      />
      
      <div className="min-h-screen flex flex-col" style={{ background: UI.bg, color: UI.text }}>
        <div className="w-full max-w-md mx-auto min-h-screen flex flex-col relative" style={{ background: UI.bg, color: UI.text }}>
        {/* Header 遮罩层 - 防止内容溢出 */}
        <div 
          className="fixed left-1/2 -translate-x-1/2 w-full max-w-md z-40"
          style={{ 
            top: 'env(safe-area-inset-top, 0px)',
            height: '160px',
            background: UI.bg,
            pointerEvents: 'none',
          }}
        />
        
        {/* Header - Fixed */}
        <div 
          className="fixed left-1/2 -translate-x-1/2 w-full max-w-md px-4 pt-6 pb-3 z-50" 
          style={{ 
            top: 'env(safe-area-inset-top, 0px)',
            background: UI.bg,
          }}
        >
          {/* 头像和积分/状态同一排 */}
          <div className="flex items-center justify-between gap-3 mb-3">
            {/* 左侧成员头像 */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide flex-1">
              {members.map((member) => {
                const av = member.avatarType === "image" && member.avatarData 
                  ? null 
                  : (AVATARS[member.avatar] || AVATARS[0]);
                const isActive = member.id === currentMemberId;
                return (
                  <button
                    key={member.id}
                    onClick={() => handleSwitchMember(member.id)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-full transition-all shrink-0"
                    style={{
                      background: isActive ? UI.primarySoft : "rgba(15,23,42,.04)",
                      border: `1px solid ${isActive ? UI.primary : UI.border}`,
                    }}
                    title={member.name}
                  >
                    <div 
                      className="w-7 h-7 rounded-full flex items-center justify-center text-sm overflow-hidden"
                      style={{
                        background: member.avatarType === "image" && member.avatarData ? 'transparent' : av?.color,
                      }}
                    >
                      {member.avatarType === "image" && member.avatarData ? (
                        <img
                          src={member.avatarData}
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        av?.emoji
                      )}
                    </div>
                    <span 
                      className="text-xs font-medium pr-1"
                      style={{ color: isActive ? UI.primary : UI.muted }}
                    >
                      {member.name}
                    </span>
                  </button>
                );
              })}
              {mode === "parent" && (
                <button
                  onClick={() => {
                    handleOpenAddMember();
                    setMemberModal(true);
                  }}
                  className="flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium shrink-0"
                  style={{
                    background: "rgba(15,23,42,.04)",
                    border: `1px dashed ${UI.border}`,
                    color: UI.muted,
                  }}
                  title="管理成员"
                >
                  <span className="text-sm">+</span>
                  <span className="pr-1">成员</span>
                </button>
              )}
            </div>

            {/* 右侧状态显示 */}
            {mode === "child" ? (
              <div
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full shrink-0"
                style={{ background: UI.primarySoft, border: `1px solid ${UI.primary}30` }}
              >
                <IconCrown className="w-4 h-4" style={{ color: UI.primary }} />
                <span className="text-sm font-bold" style={{ color: UI.primary }}>
                  {creditScore}
                </span>
              </div>
            ) : (
              <div className="shrink-0">
                {pendingCount > 0 ? (
                  <div
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                    style={{ background: "rgba(217,119,6,.10)", border: "1px solid rgba(217,119,6,.25)" }}
                  >
                    <IconBell className="w-4 h-4" style={{ color: UI.warn }} />
                    <span className="text-sm font-bold" style={{ color: UI.warn }}>
                      {pendingCount}
                    </span>
                  </div>
                ) : (
                  <div
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                    style={{ background: "rgba(15,23,42,.04)", border: `1px solid ${UI.border}` }}
                  >
                    <IconCheck className="w-4 h-4" style={{ color: UI.muted }} />
                    <span className="text-sm" style={{ color: UI.muted }}>
                      已处理
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mode switcher */}
          <div
            className="flex rounded-full overflow-hidden"
            style={{ background: UI.panel, border: `1px solid ${UI.border}`, boxShadow: "0 4px 12px rgba(15,23,42,.04)" }}
          >
            {[
              { v: "child", label: "孩子端", Icon: IconBaby },
              { v: "parent", label: "家长端", Icon: IconFamily }
            ].map(({ v, label, Icon }) => (
              <button
                key={v}
                onClick={() => {
                  setMode(v);
                  setTab("home");
                }}
                className="flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 transition-all"
                style={{
                  background: mode === v ? UI.primarySoft : "transparent",
                  color: mode === v ? UI.primary : UI.muted,
                }}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* PWA 安装提示横幅 */}
        {showInstallBanner && (
          <div 
            className="fixed top-24 left-1/2 -translate-x-1/2 w-full max-w-md px-4 z-20"
            style={{ animation: 'slideDown 0.3s ease-out' }}
          >
            <div 
              className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl"
              style={{ 
                background: UI.panel, 
                border: `1px solid ${UI.primary}30`,
                boxShadow: '0 4px 20px rgba(79,70,229,.15)'
              }}
            >
              <div className="flex items-center gap-3 flex-1">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: UI.primarySoft }}
                >
                  <IconPhone className="w-5 h-5" style={{ color: UI.primary }} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold" style={{ color: UI.text }}>
                    安装应用到主屏幕
                  </div>
                  <div className="text-xs" style={{ color: UI.muted }}>
                    像原生应用一样快速访问
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDismissInstall}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{ color: UI.muted }}
                >
                  以后
                </button>
                <button
                  onClick={handleInstallClick}
                  className="px-4 py-1.5 rounded-lg text-xs font-bold transition-all"
                  style={{ 
                    background: UI.primary, 
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(79,70,229,.3)'
                  }}
                >
                  安装
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Child mode */}
        {mode === "child" && (
          <div ref={contentRef} className="flex-1 overflow-y-auto px-4 pb-24" style={{ overscrollBehavior: 'contain', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 160px)' }}>
            {tab !== "checkins" && (
              <div className="mb-3">
                <AssetCard
                  wallet={wallet}
                  savings={savings}
                  tasks={tasks}
                />
              </div>
            )}

            <div>
              {tab === "home" && (
                <div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {[
                      {
                        label: "待接取",
                        val: tasks.filter((t) => !t.investment && !t.completed).length,
                      },
                      {
                        label: "进行中",
                        val: tasks.filter((t) => t.investment && !t.completed).length,
                      },
                      { label: "审核中", val: submissions.length },
                    ].map((s) => (
                      <Card key={s.label} className="p-3 text-center" style={{ boxShadow: "none" }}>
                        <div className="text-lg font-bold" style={{ color: UI.text }}>
                          {s.val}
                        </div>
                        <div className="text-xs" style={{ color: UI.muted }}>
                          {s.label}
                        </div>
                      </Card>
                    ))}
                  </div>

                  <div className="text-xs font-semibold mb-2" style={{ color: UI.muted }}>
                    待打卡任务
                  </div>
                  {tasks
                    .filter((t) => t.investment !== null && t.investment !== undefined && !t.completed)
                    .slice(0, 3)
                    .map((t) => (
                      <TaskCard
                        key={t.id}
                        task={t}
                        creditScore={creditScore}
                        onAccept={setAcceptModal}
                        onSubmitProof={setProofModal}
                        onEarlyWithdraw={handleEarlyWithdraw}
                      />
                    ))}
                  {tasks.filter((t) => t.investment !== null && t.investment !== undefined && !t.completed).length === 0 && (
                    <div className="text-sm py-4 text-center" style={{ color: UI.muted }}>
                      暂无待打卡任务，去任务列表接受新任务吧
                    </div>
                  )}
                </div>
              )}

              {tab === "tasks" && (
                <TaskList
                  tasks={tasks}
                  creditScore={creditScore}
                  onAccept={setAcceptModal}
                  onSubmitProof={setProofModal}
                  onEarlyWithdraw={handleEarlyWithdraw}
                />
              )}

              {tab === "checkins" && (
                <CheckinHistoryPage tasks={tasks} />
              )}

              {tab === "bank" && (
                <WalletCard
                  wallet={wallet}
                  savings={savings}
                  onSave={handleSave}
                  onWithdraw={handleWithdraw}
                  rateInfo={rateInfo}
                  rateFetching={rateFetching}
                  logs={logs}
                />
              )}

              {tab === "credit" && <CreditPanel score={creditScore} rateInfo={rateInfo} />}
            </div>

            {/* Bottom nav */}
            <div
              className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md px-3 py-2"
              style={{
                background: "rgba(246,247,251,.92)",
                backdropFilter: "blur(14px)",
                borderTop: `1px solid ${UI.border}`,
              }}
            >
              <div className="grid grid-cols-5 gap-2">
                {childTabs.map((t) => {
                  const active = tab === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => {
                        setTab(t.id);
                        setTimeout(() => {
                          if (contentRef.current) {
                            contentRef.current.scrollTop = 0;
                          }
                          window.scrollTo(0, 0);
                        }, 0);
                      }}
                      className="py-2 rounded-xl text-xs font-semibold flex flex-col items-center gap-1"
                      style={{
                        background: active ? UI.primarySoft : UI.panel,
                        border: `1px solid ${active ? "rgba(79,70,229,.20)" : UI.border}`,
                        color: active ? UI.primary : UI.muted,
                      }}
                      aria-label={t.label}
                    >
                      <NavIcon name={t.icon} active={active} />
                      <span>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Parent mode */}
        {mode === "parent" && (
          <div className="flex-1 overflow-y-auto px-4 pb-8" style={{ overscrollBehavior: 'contain', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 160px)' }}>
            {/* 统计面板 */}
            <ParentStatsPanel 
              submissions={submissions}
              tasks={tasks}
              wallet={wallet}
              savings={savings}
              creditScore={creditScore}
            />

            {/* 待审核区域 */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <IconEdit className="w-5 h-5" style={{ color: UI.primary }} />
                  <span className="text-sm font-bold" style={{ color: UI.text }}>
                    待审核凭证
                  </span>
                  {pendingCount > 0 && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: "rgba(217,119,6,.12)", color: UI.warn }}
                    >
                      {pendingCount}
                    </span>
                  )}
                </div>
              </div>

              {submissions.filter(s => s.status === "pending").length === 0 ? (
                <Card className="p-8 text-center" style={{ boxShadow: "0 2px 8px rgba(15,23,42,.04)" }}>
                  <div className="flex justify-center mb-2">
                    <IconSparkles className="w-12 h-12" style={{ color: UI.primary }} />
                  </div>
                  <div className="text-sm font-medium" style={{ color: UI.text }}>
                    暂无待审核任务
                  </div>
                  <div className="text-xs mt-1" style={{ color: UI.muted }}>
                    所有凭证已处理完毕
                  </div>
                </Card>
              ) : (
                <div className="space-y-3">
                  {submissions.filter(s => s.status === "pending").map((sub) => (
                    <ReviewCard 
                      key={sub.id} 
                      submission={sub} 
                      onApprove={handleApprove} 
                      onReject={handleReject} 
                    />
                  ))}
                </div>
              )}
            </div>

            {/* 任务管理区域 */}
            <ParentTaskManager
              tasks={tasks}
              onEdit={handleOpenEditTask}
              onDelete={handleDeleteTask}
              onCreate={() => setCreateModal(true)}
            />

            {/* 数据管理区域 */}
            <Card className="p-4 mb-4" style={{ boxShadow: "0 2px 8px rgba(15,23,42,.04)" }}>
              <div className="flex items-center gap-2 mb-3">
                <IconSave className="w-5 h-5" style={{ color: UI.primary }} />
                <span className="text-sm font-bold" style={{ color: UI.text }}>
                  数据管理
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <Button variant="subtle" onClick={handleExport} className="text-xs flex items-center justify-center gap-1">
                  <IconUpload className="w-3 h-3" /> 导出
                </Button>
                <Button variant="subtle" onClick={() => setImportModal(true)} className="text-xs flex items-center justify-center gap-1">
                  <IconDownload className="w-3 h-3" /> 导入
                </Button>
                <Button variant="danger" onClick={handleClearData} className="text-xs flex items-center justify-center gap-1">
                  <IconTrash className="w-3 h-3" /> 清空
                </Button>
              </div>
              <div className="flex items-center gap-1 text-xs p-2.5 rounded-lg" style={{ background: "rgba(15,23,42,.03)", color: UI.muted }}>
                <IconLightbulb className="w-3 h-3 flex-shrink-0" />
                数据会自动保存到浏览器，建议定期导出备份
              </div>
            </Card>

            {/* 孩子账户总览 */}
            <Card className="p-4" style={{ boxShadow: "0 2px 8px rgba(15,23,42,.04)" }}>
              <div className="flex items-center gap-2 mb-3">
                <IconUser className="w-5 h-5" style={{ color: UI.primary }} />
                <span className="text-sm font-bold" style={{ color: UI.text }}>
                  孩子账户总览
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  {
                    label: "总资产",
                    val: `¥${formatMoney(wallet + savings.balance)}`,
                    Icon: IconMoney,
                    color: UI.primary
                  },
                  {
                    label: "童信分",
                    val: creditScore,
                    Icon: IconCrown,
                    color: UI.warn
                  },
                  {
                    label: "累计利息",
                    val: `¥${formatMoney(savings.interest)}`,
                    Icon: IconTrendingUp,
                    color: UI.success
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="rounded-xl p-3 text-center"
                    style={{
                      background: `${s.color}10`,
                      border: `1px solid ${s.color}20`
                    }}
                  >
                    <div className="flex justify-center mb-0.5">
                      <s.Icon className="w-5 h-5" style={{ color: s.color }} />
                    </div>
                    <div className="text-base font-bold" style={{ color: UI.text }}>
                      {s.val}
                    </div>
                    <div className="text-xs" style={{ color: s.color }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Modals */}
      {acceptModal && (
        <AcceptTaskModal
          task={acceptModal}
          walletBalance={wallet}
          onClose={() => setAcceptModal(null)}
          onAccept={handleAccept}
        />
      )}

      {proofModal && (
        <ProofModal
          task={proofModal}
          onClose={() => setProofModal(null)}
          onSubmit={handleSubmitProof}
        />
      )}

      {checkinHistoryModal && (
        <CheckinHistoryModal
          task={checkinHistoryModal}
          onClose={() => setCheckinHistoryModal(null)}
        />
      )}

      {createModal && <CreateTaskModal onClose={() => setCreateModal(false)} onCreate={handleCreateTask} />}

      {importModal && (
        <ModalShell title="导入数据" subtitle="从备份文件恢复" onClose={() => setImportModal(false)} maxH="60vh">
          <div className="text-sm mb-4" style={{ color: UI.muted }}>
            选择之前导出的 JSON 备份文件进行导入。
          </div>
          <div
            className="rounded-2xl overflow-hidden mb-4 flex items-center justify-center cursor-pointer"
            style={{
              height: 150,
              background: "rgba(15,23,42,.03)",
              border: `1px dashed ${UI.borderStrong}`,
            }}
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.json,application/json';
              input.onchange = (e) => {
                const file = e.target.files[0];
                if (file) handleImport(file);
              };
              input.click();
            }}
          >
            <div className="text-center px-4">
              <div className="text-sm font-semibold" style={{ color: UI.text }}>
                点击选择 JSON 文件
              </div>
              <div className="text-xs mt-1" style={{ color: UI.muted }}>
                选择备份文件进行导入
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" className="flex-1" onClick={() => setImportModal(false)}>
              取消
            </Button>
          </div>
        </ModalShell>
      )}

      {memberModal && (
        <ModalShell 
          title="管理成员" 
          subtitle="添加或移除家庭成员" 
          onClose={() => {
            setMemberModal(false);
            setNewMemberForm({ name: "", avatar: 0, wallet: 120, savingsBalance: 80, creditScore: 650 });
          }} 
          maxH="80vh"
        >
          <div className="space-y-4">
            {/* 添加成员表单 */}
            <div className="p-4 rounded-xl" style={{ background: UI.panel, border: `1px solid ${UI.border}` }}>
              <div className="text-sm font-semibold mb-3" style={{ color: UI.text }}>
                添加新成员
              </div>
              <div className="space-y-3">
                {/* 名称输入 */}
                <div>
                  <label className="text-xs mb-1 block" style={{ color: UI.muted }}>
                    成员名称
                  </label>
                  <input
                    type="text"
                    value={newMemberForm.name}
                    onChange={(e) => setNewMemberForm({ ...newMemberForm, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      background: UI.bg,
                      border: `1px solid ${UI.border}`,
                      color: UI.text,
                    }}
                    placeholder="输入成员名称"
                  />
                </div>

                {/* 头像选择 */}
                <div>
                  <label className="text-xs mb-1 block" style={{ color: UI.muted }}>
                    选择头像
                  </label>
                  <div className="space-y-2">
                    {/* 预设头像 */}
                    <div className="flex gap-2">
                      {AVATARS.map((av, idx) => (
                        <button
                          key={idx}
                          onClick={() => setNewMemberForm({ ...newMemberForm, avatar: idx, avatarType: "emoji", avatarData: null })}
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-xl transition-all ${
                            newMemberForm.avatarType === "emoji" && newMemberForm.avatar === idx ? 'ring-2 ring-offset-2 scale-110' : 'opacity-60 hover:opacity-100'
                          }`}
                          style={{
                            background: av.color,
                            ringColor: av.color,
                          }}
                        >
                          {av.emoji}
                        </button>
                      ))}
                    </div>
                    
                    {/* 自定义图片上传 */}
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        id="avatar-upload"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        className="hidden"
                      />
                      <label
                        htmlFor="avatar-upload"
                        className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold text-center cursor-pointer flex items-center justify-center gap-1"
                        style={{
                          background: newMemberForm.avatarType === "image" ? UI.primarySoft : UI.bg,
                          border: `1px dashed ${newMemberForm.avatarType === "image" ? UI.primary : UI.border}`,
                          color: newMemberForm.avatarType === "image" ? UI.primary : UI.muted,
                        }}
                      >
                        {newMemberForm.avatarType === "image" ? (
                          <><IconCheck className="w-3 h-3" /> 已选择自定义图片</>
                        ) : (
                          <><IconCamera className="w-3 h-3" /> 上传自定义图片</>
                        )}
                      </label>
                      {newMemberForm.avatarType === "image" && newMemberForm.avatarData && (
                        <div
                          className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0"
                          style={{ border: `2px solid ${UI.primary}` }}
                        >
                          <img
                            src={newMemberForm.avatarData}
                            alt="预览"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 资产和信用分 */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs mb-1 block" style={{ color: UI.muted }}>
                      钱包金额
                    </label>
                    <input
                      type="number"
                      value={newMemberForm.wallet}
                      onChange={(e) => setNewMemberForm({ ...newMemberForm, wallet: e.target.value })}
                      className="w-full px-2 py-2 rounded-lg text-sm"
                      style={{
                        background: UI.bg,
                        border: `1px solid ${UI.border}`,
                        color: UI.text,
                      }}
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color: UI.muted }}>
                      储蓄金额
                    </label>
                    <input
                      type="number"
                      value={newMemberForm.savingsBalance}
                      onChange={(e) => setNewMemberForm({ ...newMemberForm, savingsBalance: e.target.value })}
                      className="w-full px-2 py-2 rounded-lg text-sm"
                      style={{
                        background: UI.bg,
                        border: `1px solid ${UI.border}`,
                        color: UI.text,
                      }}
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color: UI.muted }}>
                      信用分
                    </label>
                    <input
                      type="number"
                      value={newMemberForm.creditScore}
                      onChange={(e) => setNewMemberForm({ ...newMemberForm, creditScore: e.target.value })}
                      className="w-full px-2 py-2 rounded-lg text-sm"
                      style={{
                        background: UI.bg,
                        border: `1px solid ${UI.border}`,
                        color: UI.text,
                      }}
                      min="300"
                      max="1000"
                    />
                  </div>
                </div>

                {/* 添加按钮 */}
                <Button onClick={handleAddMember} className="w-full">
                  添加成员
                </Button>
              </div>
            </div>

            {/* 成员列表 */}
            <div>
              <div className="text-sm font-semibold mb-3" style={{ color: UI.text }}>
                现有成员（{members.length}）
              </div>
              <div className="space-y-2">
                {members.map((member) => {
                  const av = member.avatarType === "image" && member.avatarData 
                    ? null 
                    : (AVATARS[member.avatar] || AVATARS[0]);
                  return (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 rounded-xl"
                      style={{
                        background: member.id === currentMemberId ? UI.primarySoft : UI.panel,
                        border: `1px solid ${UI.border}`,
                      }}
                    >
                      <div className="flex items-center gap-3">
                        {member.avatarType === "image" && member.avatarData ? (
                          <div
                            className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0"
                            style={{ border: `2px solid ${member.id === currentMemberId ? UI.primary : UI.border}` }}
                          >
                            <img
                              src={member.avatarData}
                              alt={member.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                            style={{ background: av.color }}
                          >
                            {av.emoji}
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-semibold" style={{ color: UI.text }}>
                            {member.name}
                          </div>
                          <div className="text-xs" style={{ color: UI.muted }}>
                            ¥{formatMoney(member.wallet + member.savings.balance)} · 信用分{member.creditScore}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {member.id !== currentMemberId && (
                          <button
                            onClick={() => handleSwitchMember(member.id)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg"
                            style={{
                              background: UI.primarySoft,
                              color: UI.primary,
                            }}
                          >
                            切换
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenEditMember(member)}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg"
                          style={{
                            background: "rgba(59,130,246,0.1)",
                            color: "#3b82f6",
                          }}
                        >
                          编辑
                        </button>
                        {members.length > 1 && (
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg"
                            style={{
                              background: "rgba(239,68,68,0.1)",
                              color: "#ef4444",
                            }}
                          >
                            删除
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="ghost" className="flex-1" onClick={() => setMemberModal(false)}>
              关闭
            </Button>
          </div>
        </ModalShell>
      )}

      {editMemberId && (
        <ModalShell 
          title="编辑成员信息" 
          subtitle="修改头像和名称" 
          onClose={handleCancelEdit} 
          maxH="70vh"
        >
          <div className="space-y-4">
            {/* 编辑表单 */}
            <div className="p-4 rounded-xl" style={{ background: UI.panel, border: `1px solid ${UI.border}` }}>
              <div className="space-y-3">
                {/* 名称输入 */}
                <div>
                  <label className="text-xs mb-1 block" style={{ color: UI.muted }}>
                    成员名称
                  </label>
                  <input
                    type="text"
                    value={editMemberForm.name}
                    onChange={(e) => setEditMemberForm({ ...editMemberForm, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      background: UI.bg,
                      border: `1px solid ${UI.border}`,
                      color: UI.text,
                    }}
                    placeholder="输入成员名称"
                  />
                </div>

                {/* 头像选择 */}
                <div>
                  <label className="text-xs mb-1 block" style={{ color: UI.muted }}>
                    选择头像
                  </label>
                  <div className="space-y-2">
                    {/* 预设头像 */}
                    <div className="flex gap-2">
                      {AVATARS.map((av, idx) => (
                        <button
                          key={idx}
                          onClick={() => setEditMemberForm({ ...editMemberForm, avatar: idx, avatarType: "emoji", avatarData: null })}
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-xl transition-all ${
                            editMemberForm.avatarType === "emoji" && editMemberForm.avatar === idx ? 'ring-2 ring-offset-2 scale-110' : 'opacity-60 hover:opacity-100'
                          }`}
                          style={{
                            background: av.color,
                            ringColor: av.color,
                          }}
                        >
                          {av.emoji}
                        </button>
                      ))}
                    </div>
                    
                    {/* 自定义图片上传 */}
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        id="avatar-upload-edit"
                        accept="image/*"
                        onChange={handleAvatarUploadEdit}
                        className="hidden"
                      />
                      <label
                        htmlFor="avatar-upload-edit"
                        className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold text-center cursor-pointer flex items-center justify-center gap-1"
                        style={{
                          background: editMemberForm.avatarType === "image" ? UI.primarySoft : UI.bg,
                          border: `1px dashed ${editMemberForm.avatarType === "image" ? UI.primary : UI.border}`,
                          color: editMemberForm.avatarType === "image" ? UI.primary : UI.muted,
                        }}
                      >
                        {editMemberForm.avatarType === "image" ? (
                          <><IconCheck className="w-3 h-3" /> 已选择自定义图片</>
                        ) : (
                          <><IconCamera className="w-3 h-3" /> 上传自定义图片</>
                        )}
                      </label>
                      {(editMemberForm.avatarType === "image" && editMemberForm.avatarData) ? (
                        <div
                          className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0"
                          style={{ border: `2px solid ${UI.primary}` }}
                        >
                          <img
                            src={editMemberForm.avatarData}
                            alt="预览"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* 保存和取消按钮 */}
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSaveEditMember} className="flex-1">
                    保存
                  </Button>
                  <Button variant="ghost" onClick={handleCancelEdit} className="flex-1">
                    取消
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </ModalShell>
      )}

      {editTaskId && editTaskForm && (
        <ModalShell 
          title="编辑任务" 
          subtitle="修改任务信息" 
          onClose={handleCancelEditTask} 
          maxH="92vh"
        >
          <EditTaskModal 
            task={editTaskForm} 
            onClose={handleCancelEditTask} 
            onSave={handleSaveEditTask} 
          />
        </ModalShell>
      )}

      {/* PWA 更新提示 */}
      {updateAvailable && (
        <div
          className="fixed bottom-24 left-4 right-4 z-50 rounded-xl p-4 shadow-lg"
          style={{
            background: UI.panel,
            border: `1px solid ${UI.border}`,
            boxShadow: UI.shadow,
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: UI.primarySoft }}
            >
              <IconRotateCw className="w-5 h-5" style={{ color: UI.primary }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold mb-1" style={{ color: UI.text }}>
                发现新版本
              </div>
              <div className="text-xs mb-3" style={{ color: UI.muted }}>
                点击更新以获得最新功能和修复
              </div>
              <div className="flex gap-2">
                <Button onClick={applyUpdate} className="flex-1 text-xs">
                  立即更新
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setUpdateAvailable(false)}
                  className="flex-1 text-xs"
                >
                  稍后
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
        </div>
    </>
  );
}
